import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { X, RefreshCw, Search, Plus, MoreVertical, Edit, Trash, Eye, ChevronRight, 
         Download, Printer, Filter, Upload, BarChart2, Database, Mail, FileCheck,
         XCircle, List, Coins, Truck, Receipt, } from "lucide-react";
import { useSisConfig } from '../../contexts/SisConfigUi';
import { useVersion } from '../../hooks/useVersion';
import SqlexecBuilder from './SqlexecBuilder';
import useSqlexecData from '../../hooks/useSqlexecData';
import { logComponentError } from '../../utils/errFrontend';
import { resolvePlaceholders } from '../../utils/envioEmails';
import SqlexecMensaje from './SqlexecMensaje';
import GridMultiSelect from '../common/GridMultiSelect';
import FileGalleryManager from '../common/FileGalleryManager';
import SqlexecMasterDetail from './SqlexecMasterDetail';
import SqlexecConfirmDialog from './SqlexecConfirmDialog';
import ExportarToolbar from '../common/ExportarToolbar';
import { secureFetch } from '../../utils/authInterceptor';
import { evaluarCondicion } from '../../utils/formHelpers';
import SqlexecMasterDetailLight from './SqlexecMasterDetailLight';
import SqlexecIdTag from '../common/SqlexecIdTag';
import SqlexecPasswordAuthDialog from './SqlexecPasswordAuthDialog';

// ==========================================
// SECCIÓN 1: FUNCIONES AUXILIARES
// ==========================================

const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
};

const getIcon = (iconName) => {
  const icons = {
    eye: Eye,
    edit: Edit,
    trash: Trash,
    plus: Plus,
    printer: Printer,
    download: Download,
    'bar-chart-2': BarChart2,
    'refresh-cw': RefreshCw,
    filter: Filter,
    upload: Upload,
    mail: Mail,
    filecheck: FileCheck,
    xcircle: XCircle,
    list: List,
    coins: Coins,
    truck: Truck,
    receipt: Receipt,
  };
  return icons[iconName] || Eye;
};

const renderValor = (valor, tipo, columnConfig = {}, tabla_ui = {}) => {
  if (valor === null || valor === undefined || valor === '') {
    return '';
  }

  // status_map (opcional, por columna): presenta ciertos valores con el badge
  // de una clave de sisconfig.tabla_ui.status, sin cambiar el valor interno
  // (row, visible_when y tooltip_when siguen viendo el original).
  // Ej. {"Reservado": "P"} -> badge "En Reserva" (mismo que Remisiones).
  const statusMap = columnConfig?.status_map;
  if (statusMap && Object.prototype.hasOwnProperty.call(statusMap, valor)) {
    tipo = 'status';
    valor = statusMap[valor];
  }

  if (tipo === "string" && columnConfig.format === "date_formatted") {
    try {
      const [day, month, year] = valor.split('/');
      const d = new Date(year, parseInt(month) - 1, day);
      
      if (!isNaN(d.getTime())) {
        const partes = new Intl.DateTimeFormat('es-MX', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }).formatToParts(d);
        
        const dia = partes.find(p => p.type === 'day').value;
        const mes = partes.find(p => p.type === 'month').value.toUpperCase();
        const año = partes.find(p => p.type === 'year').value;
        
        return `${dia}/${mes}/${año}`;
      }
    } catch (error) {
      console.warn('Error parseando fecha formateada:', valor);
    }
    return valor;
  }

  if (tipo === "date") {
    try {
      const d = new Date(valor);
      if (!isNaN(d.getTime())) {
        const partes = new Intl.DateTimeFormat('es-MX', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }).formatToParts(d);
        
        const dia = partes.find(p => p.type === 'day').value;
        const mes = partes.find(p => p.type === 'month').value.toUpperCase();
        const año = partes.find(p => p.type === 'year').value;
        
        return `${dia}/${mes}/${año}`;
      }
    } catch (error) {
      console.warn('Error parseando fecha:', valor);
    }
    return valor;
  }

  if (tipo === "status") {
    const statusCatalog = tabla_ui?.status || {};
    const valorKey = String(valor).toLowerCase().trim();
    const statusConfig = statusCatalog[valor] || statusCatalog[valorKey];
    
    if (!statusConfig) {
      console.warn(`⚠️ Estado no definido: "${valor}" en sisconfig.tabla_ui.status`);
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            paddingLeft: '0.5rem',
            paddingRight: '0.5rem',
            paddingTop: '0.25rem',
            paddingBottom: '0.25rem',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: tabla_ui?.status?.fontWeight || '400',
            backgroundColor: '#f3f4f6',
            color: '#6b7280'
          }}
        >
          {String(valor)}
        </span>
      );
    }
    
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          paddingLeft: '0.5rem',
          paddingRight: '0.5rem',
          paddingTop: '0.25rem',
          paddingBottom: '0.25rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: tabla_ui?.status?.fontWeight || '400',
          backgroundColor: statusConfig.bgColor,
          color: statusConfig.textColor
        }}
      >
        {statusConfig.label}
      </span>
    );
  }

  if (tipo === "number") {
    const numValue = typeof valor === 'string' ? parseFloat(valor) : valor;
    
    if (isNaN(numValue)) {
      return valor;
    }

    const decimals = columnConfig.decimals !== undefined ? columnConfig.decimals : 2;
    const format = columnConfig.format;

    if (format === "money") {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(numValue);
    }

    if (format === "percentage") {
      return `${numValue.toFixed(decimals)}%`;
    }

    if (format === "number") {
      return new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(numValue);
    }

    if (decimals !== undefined) {
      return numValue.toFixed(decimals);
    }

    return numValue;
  }
  
  if (tipo === "image") {
    return <img src={`data:image/jpeg;base64,${valor}`} style={{ height: '32px', objectFit: 'contain' }} alt="" />;
  }

  if (valor && typeof valor === "object") {
    return valor.descripcio ?? JSON.stringify(valor);
  }

  return String(valor);
};

// ==========================================
// MOTOR DE VISIBILIDAD — MENU ACCIONES
// ==========================================
const getColorClass = (color) => {
  const colorMap = {
    red:   'text-red-600 hover:bg-red-50',
    blue:  'text-blue-600 hover:bg-blue-50',
    green: 'text-green-600 hover:bg-green-50',
    gray:  'text-gray-700 hover:bg-gray-50',
  };
  return colorMap[color] || colorMap.gray;
};

// ==========================================
// UNIVERSAL TOOLTIP — GRANDES LIGAS
// ==========================================
// tooltip_fields[].unir_lineas (configuracion por campo en la BD): muestra un
// texto de varias lineas en un solo renglon con ese separador -- el tooltip no
// conserva saltos de linea. Quita la viñeta "• " de cada linea y no pone
// separador despues de una linea que termina en ":" (ej. "Qué hacer: 1. ...").
// Sin unir_lineas el campo se muestra como siempre.
const unirLineasTooltip = (texto, separador) => {
  if (texto === null || texto === undefined) return texto;
  return String(texto).split(/\n+/)
    .map(l => l.trim().replace(/^•\s*/, ''))
    .filter(Boolean)
    .reduce((acc, l) => (acc === '' ? l : `${acc}${acc.endsWith(':') ? ' ' : separador}${l}`), '');
};

const UniversalTooltip = ({ col, row, x, y, tabla_ui }) => {
  const t = tabla_ui?.tooltip || {};
  const fields = col.tooltip_fields || [];
  const simpleValue = col.tooltip_field ? row[col.tooltip_field] : null;

  if (!simpleValue && fields.length === 0) return null;

  // Mismo patron que ContextMenu (linea 406-421): si el tooltip se saldria
  // del viewport, aparece del lado/arriba contrario del cursor en vez de
  // cortarse. Ancho estimado = maxWidth configurado (caso conservador -- si
  // el contenido real es mas angosto, simplemente no llega a tocar el
  // borde). Alto estimado = lineas de texto envuelto por campo (longitud
  // del valor / caracteres aprox. por linea al ancho configurado), no solo
  // "1 fila por campo" -- un tooltip_field con texto largo (ej. 600+
  // caracteres) necesita mucha mas altura que un campo corto para no
  // salirse del viewport (pointerEvents:'none' impide scroll interno,
  // asi que TODO el contenido debe caber en pantalla desde el principio).
  // Para los usos existentes (valores cortos, ~1 linea) el resultado es
  // equivalente al calculo anterior -- cero cambio de comportamiento ahi.
  const MARGIN = 16;
  const estimatedWidth = parseInt(t.maxWidth, 10) || 360;
  const charsPerLine = Math.max(20, Math.floor((estimatedWidth - 28) / 6.5));
  const estimatedLinesFor = (text) => Math.max(1, Math.ceil(String(text ?? '').length / charsPerLine));
  const estimatedTextLines = fields.length > 0
    ? fields.reduce((sum, f) => sum + estimatedLinesFor(row[f.field]), 0)
    : estimatedLinesFor(simpleValue);
  const estimatedHeight = 45 + estimatedTextLines * 24;

  const adjustedLeft = x + estimatedWidth + MARGIN > window.innerWidth
    ? x - estimatedWidth - MARGIN
    : x;

  const adjustedTop = y + 12 + estimatedHeight + MARGIN > window.innerHeight
    ? Math.max(MARGIN, y - estimatedHeight - 12)
    : y + 12;

  return (
    <div style={{
      position:        'fixed',
      top:             adjustedTop,
      left:            adjustedLeft,
      zIndex:          9999,
      backgroundColor: t.backgroundColor || '#1e1e2e',
      border:          `1px solid ${t.borderColor || '#e53e3e'}`,
      borderLeft:      t.borderLeft || '4px solid #e53e3e',
      borderRadius:    t.borderRadius || '8px',
      padding:         t.padding || '10px 14px',
      minWidth:        t.minWidth || '260px',
      maxWidth:        t.maxWidth || '360px',
      boxShadow:       t.boxShadow || '0 8px 32px rgba(0,0,0,0.4)',
      pointerEvents:   'none',
      lineHeight:      t.lineHeight || '1.6'
    }}>
      {/* Header */}
      <div style={{
        fontSize:      t.header?.fontSize || '0.65rem',
        fontWeight:    t.header?.fontWeight || '700',
        color:         t.header?.color || '#fc8181',
        letterSpacing: t.header?.letterSpacing || '0.08em',
        textTransform: 'uppercase',
        marginBottom:  '8px'
      }}>
        ⊗ {col.tooltip_header || col.alias}
      </div>

      {/* Campos múltiples */}
      {fields.length > 0 ? fields.map((f, i) => (
        <div key={i} style={{ marginBottom: '4px', fontSize: t.label?.fontSize || '0.72rem' }}>
          {f.label ? <span style={{ color: t.label?.color || '#a0aec0' }}>{f.label}: </span> : null}
          <span style={{ color: t.value?.color || '#ffffff', fontWeight: t.value?.fontWeight || '500' }}>
            {(f.unir_lineas ? unirLineasTooltip(row[f.field], f.unir_lineas) : row[f.field]) || '—'}
          </span>
        </div>
      )) : (
        // Campo simple
        <div style={{ fontSize: t.label?.fontSize || '0.72rem' }}>
          <span style={{ color: t.value?.color || '#ffffff', fontWeight: t.value?.fontWeight || '500' }}>
            {simpleValue}
          </span>
        </div>
      )}
    </div>
  );
};

// ==========================================
// SECCIÓN 2: DROPDOWN MENU COMPONENT
// ==========================================

// Factor de la escala automatica de la interfaz (index.js lo publica en
// --ui-escala sobre <html>); 1 sin escala. Las coordenadas del clic, de
// getBoundingClientRect y de window.innerWidth/innerHeight vienen en px reales
// de pantalla, pero el top/left de un menu se multiplica por el zoom: los menus
// las dividen entre el factor para salir junto al registro.
const leerEscalaInterfaz = () => {
  const escala = parseFloat(document.documentElement.style.getPropertyValue('--ui-escala'));
  return escala > 0 ? escala : 1;
};

const DropdownMenu = ({ item, accionesDisponibles, onAccionClick, onOpen }) => {
  const { modales_ui } = useSisConfig();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  
  const handleToggle = (e) => {
    e.stopPropagation();

    if (!isOpen) {
      // Cierra el tooltip activo al abrir el menu (incluye apertura por teclado).
      onOpen?.();
      const escala = leerEscalaInterfaz();
      const rectReal = e.target.getBoundingClientRect();
      const rect = {
        top:    rectReal.top / escala,
        bottom: rectReal.bottom / escala,
        left:   rectReal.left / escala,
        right:  rectReal.right / escala
      };
      //const menuHeight = accionesDisponibles.length * 40;
      const menuHeight = accionesDisponibles.reduce((total, accion) => {
        if (accion.type === 'divider') {
          const opcionesVisibles = accion.opciones?.filter(op => evaluarCondicion(op.visible_when, item)) || [];
          return total + 28 + opcionesVisibles.length * 40;
        }
        return total + 40;
      }, 0);
      const menuWidth = 140;
    
      let top = rect.bottom + 8;
      let left = rect.right - menuWidth;
    
      if (top + menuHeight > window.innerHeight / escala) {
        top = rect.top - menuHeight - 8;
      }
    
      if (left < 0) {
        left = rect.left;
      }
    
      setMenuPosition({ top, left });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="p-1 rounded text-gray-600 hover:text-gray-900"
        title="Acciones"
      >
        <MoreVertical size={16} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          <div 
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]"
            style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
          >
            {accionesDisponibles.map((accion) => {
              
              if (accion.type === 'divider') {
                const div = modales_ui?.dropdown?.divider || {};
                return (
                  <React.Fragment key={accion.key}>
                    {/* DIVIDER LABEL */}
                    <div style={{
                      padding:       div.padding       || '0.25rem 0.75rem',
                      fontSize:      div.fontSize      || '0.65rem',
                      fontWeight:    div.fontWeight    || '600',
                      color:         div.color         || '#9ca3af',
                      textTransform: div.textTransform || 'uppercase',
                      letterSpacing: div.letterSpacing || '0.08em',
                      borderTop:     div.borderTop     || '1px solid #e5e7eb',
                      marginTop:     div.marginTop     || '0.25rem'
                    }}>
                      {accion.label}
                    </div>

                    {/* OPCIONES HIJAS — fusionadas con atributos del padre. Cada
                        opcion puede traer su propio visible_when (ej. "Xml" oculto
                        cuando la factura cancelada no tiene acuse real) -- filtrado
                        aqui, no solo a nivel del divider completo. */}
                    {accion.opciones?.filter(op => evaluarCondicion(op.visible_when, item)).map(op => {
                      const IconComponent = getIcon(op.icon);
                      const colorClass = getColorClass(op.color || accion.color || 'gray');
                      const opcionCompleta = { ...accion, ...op, type: 'export' };
                      return (
                        <button
                          key={op.key}
                          onClick={() => { onAccionClick(opcionCompleta, item); setIsOpen(false); }}
                          className={`w-full px-4 py-2 text-left flex items-center ${colorClass}`}
                        >
                          <IconComponent size={14} className="mr-2" />
                          {op.label}
                        </button>
                      );
                    })}
                  </React.Fragment>
                );
              }

              // ← BOTÓN NORMAL
              const IconComponent = getIcon(accion.icon);
              const isDisabled = accion.disabled_when ? evaluarCondicion(accion.disabled_when, item) : false;
              const colorClass = isDisabled ? 'text-gray-300 cursor-not-allowed' : getColorClass(accion.color);

              return (
                <button
                  key={accion.key}
                  disabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return;
                    onAccionClick(accion, item);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-left flex items-center ${colorClass}`}
                  title={accion.label}
                >
                  <IconComponent size={14} className="mr-2" />
                  {accion.label}
                </button>
              );

            })}
          </div>
        </>
      )}
    </div>
  );
};

// ==========================================
// CONTEXT MENU — RIGHT CLICK
// ==========================================
const ContextMenu = ({ x, y, row, acciones, onAccionClick, onClose }) => {
  const menuRef = useRef(null);

  // ← Calcular altura real considerando dividers
  const menuHeight = acciones.reduce((total, accion) => {
    if (accion.type === 'divider') {
      const opcionesVisibles = accion.opciones?.filter(op => evaluarCondicion(op.visible_when, row)) || [];
      return total + 28 + opcionesVisibles.length * 40;
    }
    return total + 40;
  }, 0);

  const menuWidth = 160;

  // Clic y ventana en px reales; el menu se posiciona en px escalados
  const escala = leerEscalaInterfaz();
  const menuX = x / escala;
  const menuY = y / escala;

  // ← Ajustar posición si se sale de pantalla
  const adjustedTop  = menuY + menuHeight > window.innerHeight / escala ? menuY - menuHeight : menuY;
  const adjustedLeft = menuX + menuWidth  > window.innerWidth / escala  ? menuX - menuWidth  : menuX;

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]"
      style={{ top: adjustedTop, left: adjustedLeft }}
    >
      {acciones.map((accion) => {

        // ── DIVIDER con opciones hijas ──
        if (accion.type === 'divider') {
          return (
            <React.Fragment key={accion.key}>
              <div style={{
                padding: '0.25rem 0.75rem',
                fontSize: '0.65rem',
                fontWeight: '600',
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                borderTop: '1px solid #e5e7eb',
                marginTop: '0.25rem'
              }}>
                {accion.label}
              </div>
              {accion.opciones?.filter(op => evaluarCondicion(op.visible_when, row)).map(op => {
                const IconComponent = getIcon(op.icon);
                const opcionCompleta = { ...accion, ...op, type: 'export' };
                return (
                  <button
                    key={op.key}
                    onClick={() => { onAccionClick(opcionCompleta, row); onClose(); }}
                    className={`w-full px-4 py-2 text-left flex items-center text-sm ${getColorClass(op.color || accion.color || 'gray')}`}
                  >
                    <IconComponent size={14} className="mr-2" />
                    {op.label}
                  </button>
                );
              })}
            </React.Fragment>
          );
        }

        // ── BOTÓN NORMAL ──
        const IconComponent = getIcon(accion.icon);
        const isDisabled = accion.disabled_when ? evaluarCondicion(accion.disabled_when, row) : false;
        const colorClass = isDisabled ? 'text-gray-300 cursor-not-allowed' : getColorClass(accion.color);
        return (
          <button
            key={accion.key}
            disabled={isDisabled}
            onClick={() => { if (isDisabled) return; onAccionClick(accion, row); onClose(); }}
            className={`w-full px-4 py-2 text-left flex items-center text-sm ${colorClass}`}
          >
            <IconComponent size={14} className="mr-2" />
            {accion.label}
          </button>
        );
      })}
    </div>
  );
};

const TOOLBAR_COMPONENTES = {
  'exportar': ExportarToolbar,
};

// ==========================================
// COMPONENTE SEARCH INPUT (Independiente)
// ==========================================

const SearchInput = ({ onSearch, formulario_ui, botones_ui, tabla_ui, autoFocusSearch }) => {
  const [searchFocused, setSearchFocused] = useState(false);
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState('');

  useEffect(() => {
    if (autoFocusSearch && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocusSearch]);

  const handleSearch = () => {
    const value = inputRef.current?.value.toUpperCase() || '';
    setLocalValue(value);
    onSearch(value);
  };

  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
      setLocalValue('');
      onSearch('');
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className="flex items-center gap-2 flex-1">
      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
        <input
          ref={inputRef}
          type="text"
          defaultValue={localValue}
          onKeyDown={handleKeyDown}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          className="w-full border focus:outline-none"
          placeholder="Buscar..."
          style={{
            height: formulario_ui?.inputs?.height || '32px',
            fontSize: formulario_ui?.inputs?.fontSize || '0.75rem',
            fontFamily: formulario_ui?.inputs?.fontFamily || 'inherit',
            paddingLeft: '2.5rem',
            paddingRight: '2.5rem',
            paddingTop: formulario_ui?.inputs?.padding?.y || '0.5rem',
            paddingBottom: formulario_ui?.inputs?.padding?.y || '0.5rem',
            borderRadius: formulario_ui?.inputs?.borderRadius || '6px',
            borderWidth: searchFocused ? '2px' : (formulario_ui?.inputs?.borderWidth || '1px'),
            borderColor: searchFocused ? (botones_ui?.primary?.borderColor || '#3b82f6') : (formulario_ui?.inputs?.borderColor || '#d1d5db'),
            backgroundColor: formulario_ui?.inputs?.backgroundColor || '#ffffff',
            color: formulario_ui?.inputs?.textColor || '#111827',
            transition: 'border-color 150ms, border-width 150ms',
            textTransform: 'uppercase'
          }}
        />
        {localValue && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Limpiar búsqueda"
          >
            <X size={18} />
          </button>
        )}
      </div>
      <button
        onClick={handleSearch}
        style={{
          height: formulario_ui?.inputs?.height || '32px',
          width: formulario_ui?.inputs?.height || '32px',
          padding: '0',
          backgroundColor: botones_ui?.primary?.backgroundColor || '#3b82f6',
          border: 'none',
          borderRadius: formulario_ui?.inputs?.borderRadius || '6px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = botones_ui?.primary?.hoverBackgroundColor || '#2563eb';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = botones_ui?.primary?.backgroundColor || '#3b82f6';
        }}
        title="Buscar"
      >
        <Search size={16} color="#ffffff" />
      </button>
    </div>
  );
};

// ==========================================
// SECCIÓN 3: COMPONENTE PRINCIPAL
// ==========================================

const SqlexecTable = ({ 
  componentForm, 
  parentKey, 
  parentValue, 
  parentRow, 
  onRowSelect,
  selectionMode = false,
  onSelect,
  onCancel,
  fixedFilters = [],
  emptyState = 'no-data',
  autoFocusSearch = false,
  onDataReady = null,
  selectedExternalId = null,
  showSearch: showSearchProp = null,
  showFooter: showFooterProp = null,
  reactiveParentRefresh = false,
  compactToolbar = false,
  }) => {
  const { tabla_ui, formulario_ui, botones_ui, panel_ui, entorno, reloadUIConfig } = useSisConfig();
  const version = useVersion();

  // 🔥 Límite fijo desde configuración (estándar industrial)
  const LIMIT = tabla_ui?.pagination?.defaultSize || 13;

  // ==========================================
  // HOOK DE DATOS (Backend + Caché)
  // ==========================================
  const { 
    data,           
    loading,        
    columnas: hookColumnas,
    schema,         
    acciones: hookAcciones,
    idsqlexec,
    refresh,        
    loadMore,
    hasMore,
    setSearch,
    setOrder,
    setFilters,
    refreshRecord,
    ejecutarExport,
    ejecutarEmailConfig,
  } = useSqlexecData(componentForm, {
    action: 'read',
    autoFetch: true,
    enableFocusRefresh: false,
    focusThresholdSeconds: 30,
    throttleMs: 500,
    parentKey,
    parentValue,
    parentRow,
    limit: LIMIT,
    reactiveParentRefresh,
    initialFilters: fixedFilters
  });

  // ==========================================
  // GATE GENÉRICO — schema.config.requires_invepara
  // Si el componente lo declara en su config, valida que exista fila en
  // invepara para la sucursal actual ANTES de permitir usar la pantalla.
  // Config-driven -- cualquier componentForm puede activarlo, sin tocar
  // este archivo de nuevo.
  // ==========================================
  const [inveparaBlocked, setInveparaBlocked] = useState(false);
  const inveparaCheckedRef = useRef(false);

  useEffect(() => {
    if (!schema?.config?.requires_invepara) return;
    if (inveparaCheckedRef.current) return;
    inveparaCheckedRef.current = true;

    (async () => {
      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        const r = await secureFetch('/api/sqlexec/query', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: 'SELECT inventario FROM mapasoft.invepara WHERE idsucursal = :session_idsucursal LIMIT 1'
          })
        });
        const j = await r.json();

        if (!j?.success || !j?.data?.length) {
          let sucursalLabel = '';
          try {
            const session = JSON.parse(sessionStorage.getItem('mapasac_session'));
            sucursalLabel = session?.empresa?.nombre || `#${session?.user?.idsucursal ?? ''}`;
          } catch { /* noop */ }

          setInveparaBlocked(true);
          showMensajeModal(
            'error',
            'Sucursal sin parámetros de inventario configurados',
            `No se puede acceder a Remisiones: la sucursal ${sucursalLabel} no tiene parámetros de inventario configurados en invepara. Contacte al administrador para configurar.`,
            [
              'Verifica que exista una fila en mapasoft.invepara para el idsucursal de esta sucursal',
              'Si no existe, un administrador debe crearla con el valor de "inventario" correspondiente'
            ],
            { component_form: componentForm, tabla: 'mapasoft.invepara' }
          );
        }
      } catch (error) {
        console.error('❌ Error validando invepara:', error);
      }
    })();
  }, [schema, componentForm]);

  const dataReadyRef = useRef(false);

  useEffect(() => {
    if (!data?.length) return;
    if (!onDataReady) return;
    if (dataReadyRef.current) return;
    dataReadyRef.current = true;
    onDataReady(data);
  }, [data, onDataReady]);

  // 🔑 Listener sqlexec:refresh — refresca este tab si coincide componentForm
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === componentForm) {
        refresh();
      }
    };
    window.addEventListener('sqlexec:refresh', handler);
    return () => window.removeEventListener('sqlexec:refresh', handler);
  }, [componentForm]);

  useEffect(() => {
    dataReadyRef.current = false;
  }, [componentForm, parentValue]);

  // ==========================================
  // ESTADOS CORE
  // ==========================================
  const [moduleTitle, setModuleTitle] = useState('');
  const [idField, setIdField] = useState('id');
  const [accionesMenu, setAccionesMenu] = useState([]);
  const [toolbarButtons, setToolbarButtons] = useState([]);

  const [userPermissionLevel, setUserPermissionLevel] = useState('read');
  const [showFiltros, setShowFiltros]       = useState(false);
  const [filtrosValores, setFiltrosValores] = useState({});
  const [filtrosActivos, setFiltrosActivos] = useState(0);
  const [splitPanelActivo, setSplitPanelActivo] = useState(false);
  
  // Estados de modales CRUD
  const [showFormModal, setShowFormModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showConfirmaExport, setShowConfirmaExport] = useState(false);
  const [pendingExport, setPendingExport] = useState(null);
  const [showConfirmaAccion, setShowConfirmaAccion] = useState(false);
  const [pendingAccion, setPendingAccion] = useState(null);
  const [showPasswordAuth, setShowPasswordAuth] = useState(false);
  const [pendingAuthAccion, setPendingAuthAccion] = useState(null);
  const [accionAutorizada, setAccionAutorizada] = useState(false);
  

  // Estados de búsqueda
  const [searchTerm, setSearchTerm] = useState("");

  // ORDENAMIENTO DE COLUMNAS
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: null
  });

  // Ref para scroll horizontal
  const tableScrollRef = useRef(null);
  const savedScrollLeft = useRef(0);

  // Estado para selección de fila
  const [selectedRowId, setSelectedRowId] = useState(null);

  // 🎯 SISTEMA DE NAVEGACIÓN A REGISTRO (UAX)
  const [pendingScrollToId, setPendingScrollToId] = useState(null);
  const rowRefs = useRef({});
  const tableContainerRef = useRef(null);
  const savedVerticalScroll = useRef(0);

  // Estado para modal de mensajes
  const [showMensaje, setShowMensaje] = useState(false);
  const [mensajeData, setMensajeData] = useState({
    type: 'error',
    title: '',
    message: '',
    steps: [],
    technicalInfo: null
  });

  // Estado para GridMultiSelect
  const [showMultiSelect, setShowMultiSelect] = useState(false);
  const [showFileGallery, setShowFileGallery] = useState(false);
  const [showDynamicModal, setShowDynamicModal] = useState(false);
  const [dynamicModalConfig, setDynamicModalConfig] = useState(null);
  const [emailData, setEmailData] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [showMotivo, setShowMotivo] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);

  // ==========================================
  // EFECTO: Guardar scroll horizontal Mapasac
  // ==========================================
  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      savedScrollLeft.current = container.scrollLeft;
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // ==========================================
  // FUNCIONES DE PERMISOS
  // ==========================================

  const getPermiso = useCallback((componentFormParam) => {
    try {
      const sessionData = sessionStorage.getItem('mapasac_session');
      if (!sessionData) return null;
      
      const session = JSON.parse(sessionData);
      const permisos = session?.rbac?.permisos || [];
      
      let permisoEncontrado = permisos.find(p => 
        p.componente_form === componentFormParam
      );
      
      if (!permisoEncontrado) {
        const nombreLimpio = componentFormParam
          .toLowerCase()
          .replace('layout', '')
          .replace('panel_', '')
          .replace(/_/g, '');
        
        permisoEncontrado = permisos.find(p => {
          const codigoLimpio = (p.codigo || '').toLowerCase().replace(/_/g, '');
          return codigoLimpio.includes(nombreLimpio);
        });
      }

      if (!permisoEncontrado && componentFormParam.includes('_')) {
        const parts = componentFormParam.split('_');
        let parentPrefix = '';
        
        for (let i = parts.length - 1; i >= 0; i--) {
          if (parts[i] && parts[i][0] === parts[i][0].toUpperCase()) {
            parentPrefix = parts.slice(0, i).join('_');
            break;
          }
        }

        if (parentPrefix) {
          permisoEncontrado = permisos.find(p => 
            p.componente_form === parentPrefix
          );

          if (!permisoEncontrado) {
            const nombreLimpio = parentPrefix
              .toLowerCase()
              .replace('layout', '')
              .replace('panel_', '')
              .replace(/_/g, '');
            
            permisoEncontrado = permisos.find(p => {
              const codigoLimpio = (p.codigo || '').toLowerCase().replace(/_/g, '');
              return codigoLimpio.includes(nombreLimpio);
            });
          }
        }
      }
      
      return permisoEncontrado || null;
      
    } catch (error) {
      console.error('❌ Error obteniendo permiso:', error);
      return null;
    }
  }, []);

  const getUserPermissionLevel = useCallback((componentFormParam) => {
    const permiso = getPermiso(componentFormParam);
    
    if (!permiso) return 'read';
    
    if (permiso.puede_eliminar || permiso.acciones === 'full') {
      return 'full';
    }
    if (permiso.puede_actualizar || permiso.acciones === 'edit') {
      return 'edit';
    }
    return 'read';
  }, [getPermiso]);

  const getEsCatalogo = useCallback((componentFormParam) => {
    const permiso = getPermiso(componentFormParam);
    return permiso?.escatalogo ?? 1;
  }, [getPermiso]);
  
  const tienePermiso = useCallback((userLevel, requiredLevel) => {
    const jerarquia = { read: 1, edit: 2, full: 3 };
    return (jerarquia[userLevel] || 0) >= (jerarquia[requiredLevel] || 0);
  }, []);

  // ==========================================
  // Calcular nivel de permiso
  // ==========================================
  useEffect(() => {
    const level = getUserPermissionLevel(componentForm);
    setUserPermissionLevel(level);
  }, [componentForm, getUserPermissionLevel]);

  // ==========================================
  // EFECTO: Procesar schema y permisos
  // ==========================================
  useEffect(() => {
    if (!schema) return;

    setModuleTitle(schema?.title || componentForm);

    if (hookColumnas && hookColumnas.length > 0) {
      const primaryColumn = hookColumnas.find(col => col.tipo === 'id');
      if (primaryColumn) {
        setIdField(primaryColumn.campo);
      }
    }

    if (hookAcciones?.menu_acciones) {
      const accionesFiltradas = hookAcciones.menu_acciones.filter(accion => {
        return tienePermiso(userPermissionLevel, accion.permission_level);
      });
      setAccionesMenu(accionesFiltradas);
    } else {
      setAccionesMenu([]);
    }
  }, [schema, hookColumnas, hookAcciones, userPermissionLevel, tienePermiso]);

  // ==========================================
  // EFECTO: Procesar toolbar_acciones
  // ==========================================
  useEffect(() => {
    if (!hookAcciones?.toolbar_acciones) {
      setToolbarButtons([]);
      return;
    }

    const botonesVisibles = Object.entries(hookAcciones.toolbar_acciones)
      .filter(([key, config]) => {
        if (!config.active) return false;
        return tienePermiso(userPermissionLevel, config.permission_level);
      })
      .sort((a, b) => (a[1].order || 0) - (b[1].order || 0))
      .map(([key, config]) => ({ key, ...config }));
    
    setToolbarButtons(botonesVisibles);
  }, [hookAcciones, userPermissionLevel, tienePermiso]);

  // ==========================================
  // DATOS
  // ==========================================  
  const usedColumns = useMemo(() => {
    if (!Array.isArray(hookColumnas) || hookColumnas.length === 0) return [];
    return hookColumnas
      .filter(c => c.visible !== false)
      .map((c) => ({
        ...c,
        path: c.path || c.campo || c.col || c.name,
      }));
  }, [hookColumnas]);

  // accionesMenu.length>0 solo confirma que HAY acciones configuradas para
  // la tabla -- no que alguna fila realmente cumpla su visible_when. Sin
  // esto, la columna "Acciones" (header + celdas) se mostraba vacia cuando
  // ninguna fila cargada tenia una accion visible (ej. split panel de
  // partidas visto desde Facturas, donde editar_producto/editar_unidad
  // estan ocultas en todas las filas por igual).
  // Nota: en grillas con paginacion por cursor, solo evalua las filas YA
  // cargadas -- si una pagina siguiente trajera filas con acciones y la
  // actual no, el header podria aparecer al cargar mas. No aplica a split
  // panels (nunca paginan), y es un trade-off aceptado antes que agregar
  // estado adicional para un caso que hoy no ocurre.
  const hayAccionesVisibles = useMemo(() => {
    if (!Array.isArray(accionesMenu) || accionesMenu.length === 0) return false;
    return data.some(row => accionesMenu.some(a => evaluarCondicion(a.visible_when, row)));
  }, [data, accionesMenu]);

  // Fuente unica de verdad del menu contextual (clic derecho): el mismo
  // filtro decide si se abre, si se dibuja y si bloquea tooltips. Un
  // contextMenu sin acciones visibles nunca cuenta como abierto, asi que
  // no puede dejar los tooltips bloqueados.
  const accionesVisiblesDe = (row) =>
    row ? accionesMenu.filter(a => evaluarCondicion(a.visible_when, row)) : [];
  const accionesContextMenu = contextMenu ? accionesVisiblesDe(contextMenu.row) : [];
  const contextMenuAbierto = accionesContextMenu.length > 0;

  // ===================================================
  // SCROLL AL REGISTRO AL MOMENTO DE ACTIVAR SPLITPANEL
  // ===================================================
  useEffect(() => {
    const handleSplitScroll = (e) => {
      // Validamos que el evento pertenezca a esta instancia de tabla
      if (e.detail.componentForm === componentForm && e.detail.selectedId) {
        setTimeout(() => {
          const element = rowRefs.current[e.detail.selectedId];
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'nearest' 
            });
          }
        }, 150);
      }
    };

    const handleSplitClosed = (e) => {
      if (e.detail.componentForm === componentForm) {
        setSplitPanelActivo(false);
      }
    };

    const handleFiltrosCerrar = () => setShowFiltros(false);

    window.addEventListener('sqlexec:splitpanel', handleSplitScroll);
    window.addEventListener('sqlexec:splitpanel:closed', handleSplitClosed);
    window.addEventListener('sqlexec:filtros:cerrar', handleFiltrosCerrar);
    return () => {
      window.removeEventListener('sqlexec:splitpanel', handleSplitScroll);
      window.removeEventListener('sqlexec:splitpanel:closed', handleSplitClosed);
      window.removeEventListener('sqlexec:filtros:cerrar', handleFiltrosCerrar);
    };
  }, [componentForm]);

  // ==========================================
  // NAVEGACIÓN A REGISTRO (UAX)
  // ==========================================
  useEffect(() => {
    if (!pendingScrollToId || !idField || data.length === 0) return;

    const recordIndex = data.findIndex(
      row => String(row[idField]) === String(pendingScrollToId)
    );

    if (recordIndex !== -1) {
      const element = rowRefs.current[pendingScrollToId];
      if (element && tableContainerRef.current) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'center'
        });
        setPendingScrollToId(null);
      }
    } else if (hasMore && !loading) {
      loadMore();
    } else if (!hasMore) {
      toast.warning('Registro no encontrado');
      setPendingScrollToId(null);
    }
  }, [data, pendingScrollToId, idField, hasMore, loading, loadMore]);

  // ==========================================
  // HANDLERS DE UI
  // ==========================================

  const showMensajeModal = (type, title, message, steps = [], technicalInfo = null) => {
    setMensajeData({ type, title, message, steps, technicalInfo });
    setShowMensaje(true);
  };

  const handleActualizar = async () => {

    if (entorno?.toUpperCase() === 'DESARROLLO') {
      reloadUIConfig().catch(err => console.error('Error recargando UI:', err));
    }
    
    // Limpiar filtro
    setSearchTerm('');
    setSearch('');
    
    // Resetear ordenamiento
    setSortConfig({ field: null, direction: null });
    setOrder('', '');
    
    setPendingScrollToId(null);
    refresh();  // Resetea cursor y recarga primer bloque
  };

  const ejecutarCancelacion = async (accion, item, motivo) => {
    if (!accion?.funcion) return;
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const session = JSON.parse(sessionStorage.getItem('mapasac_session'));
      const usuario = session?.user?.login || '';
      const param = item?.[accion.param_field];

      if (!param) {
        toast.error(`Parámetro inválido: ${accion.param_field}`);
        return;
      }

      const body = {
        funcion: accion.funcion,
        component_form: componentForm,
        accion_key: accion.key,
        payload: {
          ...accion.fn_payload,
          pktabla: param,
          ...(typeof motivo === 'object' ? motivo : { motivo }),
          usuario,
          ...(accion._autorizado === true ? { _autorizado: true } : {})
        }
      };

      const r = await secureFetch('/api/sqlexec_generico/ejecutar_accion', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!r.ok) {
        const text = await r.text();
        console.error('❌ HTTP ERROR:', r.status, text);
        toast.error(`Error HTTP ${r.status}`);
        return;
      }

      const j = await r.json();
      if (j?.success) {
        if (accion.confirm?.exitoMensaje) {
          showMensajeModal(
            'success',
            accion.confirm.exitoTitulo || 'Operación exitosa',
            accion.confirm.exitoMensaje
          );
        } else {
          toast.success(j?.data?.message || 'Cancelación ejecutada correctamente');
        }
        // ← Cerrar SplitPanel — la remisión cancelada ya no debe quedar mostrando partidas
        if (splitPanelActivo) {
          setSplitPanelActivo(false);
          window.dispatchEvent(new CustomEvent('sqlexec:splitpanel:closed', { detail: { componentForm } }));
          window.dispatchEvent(new CustomEvent('sqlexec:splitpanel', { detail: { componentForm, selectedId: null } }));
        }
      } else {
        toast.error(j?.message || 'Error en cancelación');
      }

      refresh();
    } catch (err) {
      console.error('❌ Error cancelación:', err);
      toast.error('Error inesperado en cancelación');
    }
  };

  const ejecutarAccion = async (accion, item) => {
    if (!accion?.funcion) return;
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const param = item?.[accion.param_field];

      if (!param) {
        toast.error(`Parámetro inválido: ${accion.param_field}`);
        return;
      }

      const body = {
        funcion: accion.funcion,
        params:  [param],
        component_form: componentForm,
        accion_key: accion.key
      };

      if (accion.fn_payload || accion._autorizado === true) {
        body.payload = {
          ...accion.fn_payload,
          pk_value: param,
          ...(accion._autorizado === true ? { _autorizado: true } : {})
        };
      }

      const r = await secureFetch('/api/sqlexec_generico/ejecutar_accion', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!r.ok) {
        const text = await r.text();
        console.error("❌ HTTP ERROR:", r.status, text);
        toast.error(`Error HTTP ${r.status}`);
        return;
      }

      const j = await r.json();
      if (j?.success) {
        if (accion.confirm?.exitoMensaje) {
          showMensajeModal('success', accion.confirm.exitoTitulo || 'Operación exitosa',
            accion.confirm.exitoMensaje);
        } else {
          toast.success(j?.data?.message || 'Proceso ejecutado');
        }
      } else {
        toast.error(j?.message || 'Error');
      }
      // refresh tabs independientes y huerfanos
      if (accion.fn_payload?.refresh_tabs) {
        accion.fn_payload.refresh_tabs.forEach(tab => {
          window.dispatchEvent(new CustomEvent('sqlexec:refresh', { 
            detail: tab 
          }));
        });
      }
      refresh();
    } catch (err) {
      toast.error('Error inesperado');
    }
  };

  const handleToolbarAction = async (key, action) => {

    if (action?.requires_data && (!data || data.length === 0)) {
      //toast.warning('⚠️ No hay registros para esta acción');
      return;
    }
    
    if (key === 'crear' && action?.mode === 'multiselect') {
      setShowMultiSelect(true);
      return;
    }

    if (key === 'crear' && action?.mode === 'fileGallery') {
      setShowFileGallery(true);
      return;
    }

    if (action?.type === 'filter') {
      // ← AGREGAR — si splitpanel está activo, cerrarlo
      if (splitPanelActivo) {
        setSplitPanelActivo(false);
        window.dispatchEvent(new CustomEvent('sqlexec:splitpanel:closed', {
          detail: { componentForm }
        }));
        window.dispatchEvent(new CustomEvent('sqlexec:splitpanel', {
          detail: { componentForm, selectedId: null }
        }));
      }
      setShowFiltros(prev => !prev);
      return;
    }

    if (action?.type === 'splitpanel') {
      if (action.requires_selection && !selectedRowId) {
        toast.warning('⚠️ Selecciona primero un registro');
        return;
      }

      // ← AGREGAR — si filtros está abierto, cerrarlo
      if (showFiltros) setShowFiltros(false);

      setSplitPanelActivo(prev => !prev);
      {
        const selectedParentRow = data.find(r => r[idField] === selectedRowId) || null;
        window.dispatchEvent(new CustomEvent('sqlexec:splitpanel', {
          detail: {
            componentForm,
            selectedId:  selectedParentRow ? selectedParentRow[action.split_key] : null,
            selectedRow: selectedParentRow,
            detailForm:  action.detail_form,
            splitKey:    action.split_key,
            splitRatio:  action.split_ratio,
            detailOnly:  action.detail_only
          }
        }));
      }
      return;
    }

    if (action?.type === 'modal') {
      if (action.requires_selection && !selectedRowId) {
        toast.warning('⚠️ Selecciona primero un registro');
        return;
      }
      if (action.idreporte) {
        const token = sessionStorage.getItem('mapasac_access_token');
        const r = await secureFetch(`/api/sqlexec_generico/schema/${action.component_form}?idreporte=${action.idreporte}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const j = await r.json();
        const config = j?.data?.emailConfig || null;
        const selectedParentRow = data.find(row => row[idField] === selectedRowId) || null;
        setEmailData({
          ...(selectedParentRow || {}),
          asunto:  config ? resolvePlaceholders(config.asunto,  selectedParentRow) : '',
          mensaje: config ? resolvePlaceholders(config.mensaje, selectedParentRow) : '',
          para:    config?.para ? resolvePlaceholders(config.para, selectedParentRow) : '',
          cc:      config?.cc   ? resolvePlaceholders(config.cc,   selectedParentRow) : ''
        });
      }
      setDynamicModalConfig(action);
      setShowDynamicModal(true);
      return;
    }

    const actionHandlers = {
      'crear': handleCreate,
      'actualizar': handleActualizar,
    };

    if (actionHandlers[key]) {
      actionHandlers[key]();
      return;
    }

    toast.info(`Función "${key}" en desarrollo`);
  };

  const handleCreate = useCallback(() => {
    setShowFormModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowFormModal(false);
  }, []);

  const handleSuccessModal = useCallback(async (createdId = null, createdRow = null) => {
    setShowFormModal(false);
    if (!createdId) return;

    // ← Limpiar filtros activos
    setFiltrosValores({});
    setFiltrosActivos(0);
    setShowFiltros(false);
    setSearch('');
    setSearchTerm('');
    setFilters([]);

    setSelectedRowId(createdId);
    // 🔥 Propagar la fila recien creada al padre (SqlexecPanel), igual que un click manual --
    // sin esto, un tab hijo (ej. Lineas de una Familia nueva) no ve la fila nueva como padre
    // hasta que el usuario la clickea manualmente en el grid.
    if (createdRow && onRowSelect) {
      onRowSelect(createdRow);
    }
    refresh({ orderBy: idField, orderDir: 'desc' });
  }, [refresh, idField, onRowSelect]);

  const handleAccionClick = async (accion, item, ) => {
    // Cierra cualquier tooltip abierto: clic derecho (ContextMenu) no dispara
    // onMouseLeave de la celda, asi que el tooltip quedaba pegado encima del
    // modal de edicion (Editar Cliente/Producto/Unidad, etc.).
    setActiveTooltip(null);

    // Gate de autorizacion -- un solo lugar de config,
    // menu_acciones[].requiere_autorizacion. Si ya viene autorizada
    // (segunda pasada tras el modal de contrasena), sigue de largo.
    if (accion.requiere_autorizacion && !accion._autorizado) {
      setPendingAuthAccion({ accion, item });
      setShowPasswordAuth(true);
      return;
    }
    setAccionAutorizada(accion._autorizado === true);

    // Modal informativo (SqlexecMensaje) con datos de la fila -- solo lectura
    if (accion.type === 'mensaje') {
      showMensajeModal(accion.tipo || 'info', accion.titulo || accion.label,
                       item[accion.mensaje_field] || '—', [],
                       accion.info_field ? item[accion.info_field] : null);
      return;
    }

    // ← AGREGAR — export: pdf/excel/email
    if (accion.type === 'export') {
      setSelectedItem(item);
      setSelectedRowId(item[idField]);

      if (accion.formato === 'email') {
        const emailData = await ejecutarEmailConfig(accion, item, idField);
        setEmailData(emailData);
        setDynamicModalConfig(accion);
        setShowDynamicModal(true);
        return;
      }

      if (accion.confirma) {
        // ← Reutiliza el flujo que ya existe
        setPendingExport(accion);
        setShowConfirmaExport(true);
        return;
      }

      // param_field permite exportar un registro RELACIONADO (ej. la
      // remision/factura de un renglon de detalle) en vez del id propio
      // de la fila -- mismo patron ya usado por CRUD (accion.param_field).
      ejecutarExport(accion, item[accion.param_field] ?? item[idField]);
      return;
    }

    if (accion.type === 'modal') {
      setSelectedItem(item);
      setSelectedRowId(item[idField]);
      
      if (accion.confirm_only) {
        setPendingAccion(accion);
        accion.motivo ? setShowMotivo(true) : setShowConfirmaAccion(true);
        return;
      }

      if (accion.component_form) {
        setDynamicModalConfig(accion);
        setShowDynamicModal(true);
        return;
      }
      
      ejecutarAccion(accion, item);
      return;
    }

    if (accion.validate && accion.modal_mode === 'update') {
      const { field, value, message } = accion.validate;
      if (item[field] === value) {
        toast.warning(`⚠️ ${message}`);
        return;
      }
    }

    if (accion.type === 'crud') {
      switch (accion.modal_mode) {
        case 'view':   handleView(item);   break;
        case 'update': handleEdit(item, accion.component_form_override || null, accion.key === 'edit' ? null : accion.label, accion.surtir_mode === true, accion.refresh_tabs, accion.param_field || null);  break;
        case 'delete': handleDelete(item); break;
        default: console.warn('Modal mode no reconocido:', accion.modal_mode);
      }
    }
  };

  // ==========================================
  // HANDLERS CRUD
  // ==========================================

  const handleView = async (item) => {
    try {
      if (tableContainerRef.current) {
        savedVerticalScroll.current = tableContainerRef.current.scrollTop;
      }
      
      const token = sessionStorage.getItem('mapasac_access_token');
      const itemId = item[idField];
      
      if (!itemId) {
        showMensajeModal(
          'error',
          'ID no definido',
          'No se pudo identificar el registro a consultar.',
          [
            'Verifica que la tabla tiene un campo ID configurado',
            'Contacta al administrador del sistema'
          ],
          { component_form: componentForm, error: 'ID is undefined' }
        );
        return;
      }
      
      if (!schema?.fields?.length) {
        showMensajeModal(
          'error',
          'Schema no configurado',
          `El formulario para consultar "${componentForm}" no está configurado.`,
          [
            'Accede a PostgreSQL → base de datos "empresas"',
            `Verifica el campo schema_form en la tabla mapasoft.sqlexec para '${componentForm}'`,
            'Debe contener JSON con estructura: { "fields": [...] }',
            'Contacta al administrador del sistema'
          ],
          { component_form: componentForm, error: 'schema_form is empty' }
        );
        return;
      }
      
      const response = await secureFetch(`/api/sqlexec/${componentForm}/read/${itemId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const json = await response.json();
    
      if (json.success) {
        setSelectedItem(json.data);
        setShowViewModal(true);
      } else {
        showMensajeModal(
          response.status === 404 ? 'warning' : 'error',
          response.status === 404 ? 'Registro no encontrado' : 'Error al consultar',
          json.message || 'No se pudo consultar el registro.',
          [
            'Verifica que tienes permisos para esta acción',
            'Presiona "Actualizar" para recargar los datos',
            'Si el error persiste, contacta al administrador'
          ],
          {
            component_form: componentForm,
            error: json.message,
            code: json.code,
            status: response.status
          }
        );
        
        if (response.status === 404) {
          refresh();
        }
      }
    } catch (error) {
      logComponentError('SqlexecTable', 'view_record', 
        `${componentForm} - ${error?.message || 'Unknown error'}`);
      
      showMensajeModal(
        'error',
        'Error de conexión',
        'No se pudo conectar con el servidor.',
        [
          'Verifica tu conexión a internet',
          'Asegúrate de que el servidor backend esté funcionando',
          'Si el problema persiste, contacta al administrador'
        ],
        { component_form: componentForm, error: error?.message }
      );
    }
  };

  const handleEdit = async (item, componentFormOverride = null, titleOverride = null, surtirMode = false, refreshTabs = null, idFieldOverride = null) => {
    try {
      if (tableContainerRef.current) {
        savedVerticalScroll.current = tableContainerRef.current.scrollTop;
      }

      const token = sessionStorage.getItem('mapasac_access_token');
      const itemId = item[idFieldOverride || idField];

      if (!itemId) {
        showMensajeModal(
          'error',
          'ID no definido',
          'No se pudo identificar el registro a editar.',
          [
            'Verifica que la tabla tiene un campo ID configurado',
            'Contacta al administrador del sistema'
          ],
          { component_form: componentForm, error: 'ID is undefined' }
        );
        return;
      }
      
      // Cuando hay override, el schema de ESTA tabla (ej. Cxc_Diario, 0 campos
      // propios) es irrelevante -- el formulario real lo trae SqlexecBuilder
      // por su cuenta al montarse con el componentForm del override.
      if (!componentFormOverride && !schema?.fields?.length) {
        showMensajeModal(
          'error',
          'Schema no configurado',
          `El formulario para editar "${componentForm}" no está configurado.`,
          [
            'Accede a PostgreSQL → base de datos "empresas"',
            `Verifica el campo schema_form en en la tabla mapasoft.sqlexec para '${componentForm}'`,
            'Debe contener JSON con estructura: { "fields": [...] }',
            'Contacta al administrador del sistema'
          ],
          { component_form: componentForm, error: 'schema_form is empty' }
        );
        return;
      }

      const response = await secureFetch(`/api/sqlexec/${componentFormOverride || componentForm}/read/${itemId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const json = await response.json();
    
      if (json.success) {
        const dataWithTimestamp = {
          ...json.data,
          _original_updated_at: json.data.actualizada || new Date().toISOString()
        };
        setSelectedItem({
          ...dataWithTimestamp,
          _component_form_override: componentFormOverride || undefined,
          _title_override: titleOverride || undefined,
          _surtir_mode: surtirMode,
          _refresh_tabs: refreshTabs || null,
          // Con override, dataWithTimestamp es el registro del catalogo relacionado
          // (ej. Inv_Productos), no la fila de esta tabla -- se pierde el idField
          // original (ej. idclierede). Se preserva aqui para poder identificar la
          // fila real tras guardar.
          _origin_record_id: componentFormOverride ? item[idField] : undefined
        });
        //console.log('🔍 Editando con surtirMode:', surtirMode);

        setShowEditModal(true);
      } else {
        showMensajeModal(
          response.status === 404 ? 'warning' : 'error',
          response.status === 404 ? 'Registro no encontrado' : 'Error al cargar',
          json.message || 'No se pudo cargar el registro para editarlo.',
          [
            'Verifica que tienes permisos para esta acción',
            'Presiona "Actualizar" para recargar los datos',
            'Si el error persiste, contacta al administrador'
          ],
          {
            component_form: componentForm,
            error: json.message,
            code: json.code,
            status: response.status
          }
        );
        
        if (response.status === 404) {
          refresh();
        }
      }
    } catch (error) {
      console.error('Error:', error);
      logComponentError('SqlexecTable', 'edit_record', 
        `${componentForm} - ${error?.message || 'Unknown error'}`);
      
      showMensajeModal(
        'error',
        'Error de conexión',
        'No se pudo conectar con el servidor.',
        [
          'Verifica tu conexión a internet',
          'Asegúrate de que el servidor backend esté funcionando',
          'Si el problema persiste, contacta al administrador'
        ],
        { component_form: componentForm, error: error?.message }
      );
    }
  };

  const handleDelete = async (item) => {
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const itemId = item[idField];
      
      if (!itemId) {
        showMensajeModal(
          'error',
          'ID no definido',
          'No se pudo identificar el registro a eliminar.',
          [
            'Verifica que la tabla tiene un campo ID configurado',
            'Contacta al administrador del sistema'
          ],
          { component_form: componentForm, error: 'ID is undefined' }
        );
        return;
      }
      
      try {
        const response = await secureFetch(`/api/sqlexec/${componentForm}/read/${itemId}`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        const json = await response.json();
        
        if (json.success) {
          setSelectedItem(json.data);
        } else {
          setSelectedItem(item);
        }
      } catch (readError) {
        console.warn('No se pudo leer registro completo, usando datos de tabla:', readError);
        setSelectedItem(item);
      }
      
      setShowDeleteModal(true);
      
    } catch (error) {
      console.error('Error:', error);
      logComponentError('SqlexecTable', 'delete_record', 
        `${componentForm} - ${error?.message || 'Unknown error'}`);
      
      showMensajeModal(
        'error',
        'Error inesperado',
        'Ocurrió un error al intentar preparar la eliminación.',
        [
          'Verifica tu conexión a internet',
          'Presiona "Actualizar" para recargar los datos',
          'Si el problema persiste, contacta al administrador'
        ],
        { component_form: componentForm, error: error?.message }
      );
    }
  };

  // ==========================================
  // 🔥 HANDLER: Cargar más (único mecanismo de navegación)
  // ==========================================
  
  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadMore();
    }
  };

  const handleSort = (campo) => {
    setSortConfig(prev => {
      let newDir;
      if (prev.field === campo) {
        if (prev.direction === 'asc') newDir = 'desc';
        else if (prev.direction === 'desc') { 
          setOrder('', '');
          return { field: null, direction: null };
        }
      } else {
        newDir = 'asc';
      }
      setOrder(campo, newDir);
      return { field: campo, direction: newDir };
    });
  };
  
  // ==========================================
  // TOOLBAR COMPONENT
  // ==========================================
  const Toolbar = () => {
    const btnBase = botones_ui || {};
    const btnPrimary = botones_ui?.primary || {};
    const btnSecondary = botones_ui?.secondary || {};
    const btnDanger = botones_ui?.danger || {};
    
    //?????const showSearch = tabla_ui?.toolbar?.showSearch !== false;
    const showSearch = showSearchProp !== null 
      ? showSearchProp 
      : tabla_ui?.toolbar?.showSearch !== false;

    const getButtonConfig = (color) => {
      const colorMap = {
        'blue': btnPrimary,
        'gray': btnSecondary,
        'red': btnDanger
      };
      return colorMap[color] || btnSecondary;
    };

    return (
      <div className="bg-white px-3"
        style={{ paddingTop: compactToolbar ? '0' : (tabla_ui?.header?.padding?.y || '0.5rem'),
                 paddingBottom: compactToolbar ? '0' : (tabla_ui?.header?.padding?.y || '0.5rem') }}>
        <div className="flex items-center justify-between">
          {showSearch && (
            <SearchInput
              onSearch={(value) => {
                setSearchTerm(value);
                setSearch(value);
                setPendingScrollToId(null);
              }}
              formulario_ui={formulario_ui}
              botones_ui={botones_ui}
              tabla_ui={tabla_ui}
              autoFocusSearch={autoFocusSearch}
            />
          )}

          <div className="flex items-center space-x-3">
            {toolbarButtons.map((btn) => {
              const ComponenteEspecial = TOOLBAR_COMPONENTES[btn.key];
              if (ComponenteEspecial) {
                return (
                  <ComponenteEspecial
                    key={btn.key}
                    selectedRow={selectedRowId}
                    config={btn}
                    btnBase={btnBase}
                    getButtonConfig={getButtonConfig}
                    disabled={btn.requires_data && (!data || data.length === 0)}                    
                    onConfirma={(opcion) => {
                      setPendingExport(opcion);
                      setShowConfirmaExport(true);
                    }}
                  />
                );
              }

              const IconComponent = getIcon(btn.icon);
              const isActualizar = btn.key === 'actualizar';
              const isCrear = btn.key === 'crear';
              const isDisabled = (loading && isActualizar) || (showFormModal && isCrear) ||
                    (btn.requires_data && (!data || data.length === 0));
              const isFilterActive = btn.type === 'filter' && filtrosActivos > 0;
              const isSplitActive = btn.type === 'splitpanel' && splitPanelActivo;
              const btnConfig = isFilterActive || isSplitActive ? btnPrimary : getButtonConfig(btn.color);
              
              return (
                <button
                  key={btn.key}
                  onClick={() => handleToolbarAction(btn.key, btn)}
                  disabled={isDisabled}
                  className="transition-colors"
                  style={{
                    height: btnBase.height || '32px',
                    fontSize: btnBase.fontSize || '0.75rem',
                    fontFamily: btnBase.fontFamily || 'inherit',
                    fontWeight: btnBase.fontWeight || '500',
                    paddingLeft: btnBase.padding?.x || '1rem',
                    paddingRight: btnBase.padding?.x || '1rem',
                    paddingTop: btnBase.padding?.y || '0.5rem',
                    paddingBottom: btnBase.padding?.y || '0.5rem',
                    borderRadius: btnBase.borderRadius || '6px',
                    borderWidth: btnBase.borderWidth || '1px',

                    borderColor: isDisabled 
                      ? (btnConfig.disabledBorderColor || '#9ca3af')
                      : (isFilterActive || isSplitActive)
                        ? (btnPrimary.borderColor || '#3b82f6')
                        : (btnConfig.borderColor || '#3b82f6'),
                    backgroundColor: isDisabled
                      ? (btnConfig.disabledBackgroundColor || '#9ca3af')
                      : (isFilterActive || isSplitActive)
                        ? (btnPrimary.backgroundColor || '#3b82f6')
                        : (btnConfig.backgroundColor || '#ffffff'),
                    color: isDisabled
                      ? (btnConfig.disabledTextColor || '#ffffff')
                      : (isFilterActive || isSplitActive)
                        ? (btnPrimary.textColor || '#ffffff')
                        : (btnConfig.textColor || '#374151'),

                    transitionDuration: btnBase.transitionDuration || '150ms',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    display: btnBase.display || 'flex',
                    alignItems: btnBase.alignItems || 'center',
                    justifyContent: btnBase.justifyContent || 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!isDisabled) {
                      e.target.style.backgroundColor = btnConfig.hoverBackgroundColor || '#2563eb';
                      e.target.style.borderColor = btnConfig.hoverBorderColor || '#2563eb';
                      e.target.style.color = btnConfig.hoverTextColor || '#ffffff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDisabled) {
                      e.target.style.backgroundColor = btnConfig.backgroundColor || '#3b82f6';
                      e.target.style.borderColor = btnConfig.borderColor || '#3b82f6';
                      e.target.style.color = btnConfig.textColor || '#ffffff';
                    }
                  }}
                  title={btn.label}
                >
                  {IconComponent && (
                    <IconComponent 
                      size={16} 
                      className={loading && isActualizar ? 'animate-spin' : ''} 
                    />
                  )}
                  <span>
                    {btn.label}
                    {btn.type === 'filter' && filtrosActivos > 0 
                      ? ` (${filtrosActivos})` 
                      : ''}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ==========================================
  // DATA TABLE COMPONENT
  // ==========================================

  // Funcion de render, no componente: definida dentro de SqlexecTable, como
  // componente cambiaba de identidad en cada render y React desmontaba y
  // volvia a montar toda la tabla (perdia el estado local de DropdownMenu).
  // No usa hooks, por eso llamarla como funcion es valido.
  const renderDataTable = () => {
  const tableData = data;

  const headerConfig = tabla_ui?.header || {};
  const rowsConfig = tabla_ui?.rows || {};
  const columnsConfig = tabla_ui?.columns || {};

  return (
    <div style={{
      backgroundColor: tabla_ui?.container?.tableWrapper?.backgroundColor || '#ffffff',
      overflow: tabla_ui?.container?.tableWrapper?.overflow || 'visible',
    }}>
      <div ref={tableContainerRef}>
        <table
          className="min-w-full text-sm table-auto"
          style={{
            border: tabla_ui?.table?.border || '1px solid #d1d5db',
            borderCollapse: tabla_ui?.table?.borderCollapse || 'collapse',
          }}
        >
          <thead
            className={headerConfig.sticky ? 'sticky' : ''}
            style={{
              backgroundColor: headerConfig.backgroundColor || '#f3f4f6',
              top: headerConfig.sticky ? (headerConfig.stickyTop || '0') : undefined,
              zIndex: headerConfig.sticky ? 10 : undefined
            }}
          >
            <tr>
              {usedColumns.map((col, idx) => {
                const getHeaderAlign = () => {
                  if (headerConfig.textAlign) return headerConfig.textAlign;
                  const tipo = col.tipo;
                  const format = col.format;
                  if (format === 'money') return tabla_ui?.columnAlignment?.money || 'right';
                  if (format === 'percentage') return tabla_ui?.columnAlignment?.percentage || 'right';
                  return tabla_ui?.columnAlignment?.[tipo] || 'left';
                };

                return (
                  <th
                    key={idx}
                    onClick={() => handleSort(col.path)}
                    className="whitespace-nowrap cursor-pointer select-none"
                    style={{
                      width: col.width || 'auto',
                      paddingLeft: headerConfig.padding?.x || '0.50rem',
                      paddingRight: headerConfig.padding?.x || '0.50rem',
                      paddingTop: headerConfig.padding?.y || '0.50rem',
                      paddingBottom: headerConfig.padding?.y || '0.50rem',
                      fontSize: headerConfig.fontSize || '0.75rem',
                      fontFamily: headerConfig.fontFamily || 'inherit',
                      fontWeight: headerConfig.fontWeight || '600',
                      textTransform: headerConfig.textTransform || 'uppercase',
                      textAlign: getHeaderAlign(),
                      color: headerConfig.color || '#374151',
                      height: headerConfig.height || 'auto',
                      borderBottom: headerConfig.borderBottom
                        ? `${headerConfig.borderBottom.width} ${headerConfig.borderBottom.style} ${headerConfig.borderBottom.color}`
                        : '2px solid #e5e7eb',
                      borderRight: headerConfig.borderRight || '1px solid #d1d5db',
                      borderTop: headerConfig.borderTop || '1px solid #d1d5db'
                    }}
                  >
                    <div className="flex items-center gap-1" style={{
                      justifyContent: headerConfig.textAlign === 'center' ? 'center' :
                        headerConfig.textAlign === 'right' ? 'flex-end' : 'flex-start'
                    }}>
                      {col.alias || col.header || col.path}
                      {sortConfig.field === col.path && (
                        <span style={{ fontSize: "0.7rem" }}>
                          {sortConfig.direction === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
              {(!selectionMode && tabla_ui?.actionsColumn?.show !== false && hayAccionesVisibles) && (
                <th
                  className="whitespace-nowrap"
                  style={{
                    paddingLeft: tabla_ui?.actionsColumn?.header?.padding?.x || '0.75rem',
                    paddingRight: tabla_ui?.actionsColumn?.header?.padding?.x || '0.75rem',
                    paddingTop: tabla_ui?.actionsColumn?.header?.padding?.y || '0.50rem',
                    paddingBottom: tabla_ui?.actionsColumn?.header?.padding?.y || '0.50rem',
                    fontSize: tabla_ui?.actionsColumn?.header?.fontSize || '0.75rem',
                    fontFamily: tabla_ui?.actionsColumn?.header?.fontFamily || 'inherit',
                    fontWeight: tabla_ui?.actionsColumn?.header?.fontWeight || '600',
                    textTransform: tabla_ui?.actionsColumn?.header?.textTransform || 'uppercase',
                    textAlign: tabla_ui?.actionsColumn?.header?.textAlign || 'center',
                    color: tabla_ui?.actionsColumn?.header?.color || '#374151',
                    height: headerConfig.height || 'auto',
                    borderBottom: headerConfig.borderBottom
                      ? `${headerConfig.borderBottom.width} ${headerConfig.borderBottom.style} ${headerConfig.borderBottom.color}`
                      : '2px solid #e5e7eb',
                    borderRight: headerConfig.borderRight || '1px solid #d1d5db',
                    borderTop: headerConfig.borderTop || '1px solid #d1d5db'
                  }}
                >
                  {tabla_ui?.actionsColumn?.label || 'Acciones'}
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td
                  colSpan={(usedColumns.length || 1) + 1}
                  className="px-4 py-6"
                >
                  <div className="flex flex-col items-center justify-center gap-2 text-xs">
                    {emptyState === 'no-filter' && (
                      <>
                        <Filter size={18} className="text-amber-500" />
                        <span className="text-amber-600 font-medium">
                          Selecciona un registro del Tab padre para ver datos
                        </span>
                      </>
                    )}
                    {emptyState === 'no-data' && (
                      <>
                        <Database size={18} className="text-gray-400" />
                        <span className="text-gray-500">
                          No hay registros para el TAB seleccionado
                        </span>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              tableData.map((row, rIdx) => {
                const rowId = row[idField];

                // acciones agrupadas -- cuando sqlexec_acciones.agrupar_acciones
                // esta activo, el menu de acciones (documento completo, no por
                // renglon) se oculta en las filas que son continuacion del mismo
                // grupo visual (mismas columnas agrupar_visual que la fila
                // anterior) -- reutiliza el mismo criterio, aplicado como
                // "columna virtual" de acciones.
                const columnasAgrupables = usedColumns.filter(c => c.agrupar_visual);
                const filaEsContinuacionDeGrupo = Boolean(
                  hookAcciones?.agrupar_acciones && rIdx > 0 && columnasAgrupables.length > 0 &&
                  columnasAgrupables.every(col => {
                    const prevRow = tableData[rIdx - 1];
                    let v = row[col.alias];
                    if (v === undefined && col.campo) v = getByPath(row, col.campo);
                    if (v === undefined && col.path) v = getByPath(row, col.path);
                    let pv = prevRow[col.alias];
                    if (pv === undefined && col.campo) pv = getByPath(prevRow, col.campo);
                    if (pv === undefined && col.path) pv = getByPath(prevRow, col.path);
                    return String(pv ?? '') === String(v ?? '');
                  })
                );

                const backgroundColor = rIdx % 2 === 0
                  ? (rowsConfig.odd || '#ffffff')
                  : (rowsConfig.even || '#f9fafb');
                const isSelected = String(selectedRowId) === String(rowId) || String(selectedExternalId) === String(rowId);
                const rowSelectionConfig = tabla_ui?.rowSelection;
                const selectionEnabled = rowSelectionConfig?.enabled !== false;
                let rowBackground = backgroundColor;
                if (isSelected && selectionEnabled) {
                  rowBackground = rowSelectionConfig?.backgroundColor || '#ccfbf1';
                }

                return (
                  <tr
                    key={rowId ?? `row-${rIdx}`}
                    ref={el => { if (el) rowRefs.current[rowId] = el; }}
                    onClick={() => {
                      if (selectionMode) {
                        onSelect?.(row);
                      } else if (selectionEnabled) {
                        handleRowClick(row);
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      handleRowClick(row);
                      // Sin acciones visibles no hay menu: no asignar contextMenu
                      // ni tocar el tooltip.
                      if (accionesVisiblesDe(row).length === 0) return;
                      // El clic derecho no dispara onMouseLeave: cerrar el tooltip
                      // aqui para que no tape el ContextMenu.
                      setActiveTooltip(null);
                      setContextMenu({ x: e.clientX, y: e.clientY, row });
                    }}
                    className="transition-colors"
                    style={{
                      backgroundColor: rowBackground,
                      height: rowsConfig.height || '32px',
                      borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb',
                      borderLeft: isSelected && selectionEnabled
                        ? `${rowSelectionConfig?.borderLeft?.width || '4px'} solid ${rowSelectionConfig?.borderLeft?.color || '#14b8a6'}`
                        : 'none',
                      cursor: selectionEnabled ? (rowSelectionConfig?.cursor || 'pointer') : 'default',
                      transition: rowSelectionConfig?.transition || 'all 150ms ease-in-out'
                    }}
                    onMouseEnter={(e) => {
                      if (isSelected && rowSelectionConfig?.hoverDisabled) return;
                      e.currentTarget.style.backgroundColor = rowsConfig.hoverColor || '#f0f7ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = rowBackground;
                    }}
                  >
                    {usedColumns.map((col, cIdx) => {

                      let val = row[col.alias];
                      if (val === undefined && col.campo) val = getByPath(row, col.campo);
                      if (val === undefined && col.path) val = getByPath(row, col.path);

                      // Agrupacion visual -- si esta columna repite el mismo valor
                      // que la fila inmediata anterior (ej. Pedido/Remision/Factura
                      // de varias formas de pago del mismo folio), se oculta para no
                      // repetirlo, igual que Excel/Crystal Reports. Depende del orden
                      // actual de la tabla -- si se reordena, se recalcula sobre el
                      // nuevo orden (comportamiento esperado).
                      if (col.agrupar_visual && rIdx > 0) {
                        const prevRow = tableData[rIdx - 1];
                        let prevVal = prevRow[col.alias];
                        if (prevVal === undefined && col.campo) prevVal = getByPath(prevRow, col.campo);
                        if (prevVal === undefined && col.path) prevVal = getByPath(prevRow, col.path);
                        if (String(prevVal ?? '') === String(val ?? '')) val = '';
                      }

                      // ocultar_cero -- columnas numericas donde un 0 no aporta
                      // informacion (ej. Comision/Su Cambio cuando no aplica) se
                      // muestran vacias en vez de "$0.00".
                      if (col.ocultar_cero && val !== '' && val !== null && val !== undefined) {
                        const numVal = typeof val === 'string' ? parseFloat(val) : val;
                        if (!isNaN(numVal) && numVal === 0) val = '';
                      }

                      // 🔥 Resolver decimals dinámico
                      const colResolved = { ...col };
                      if (col.displayDecimals === 'dynamic' && col.inputMask?.dynamicField) {
                        colResolved.decimals = parseInt(row[col.inputMask.dynamicField]) || 0;
                      }

                      const displayValue = renderValor(val, col.tipo, colResolved, tabla_ui);
                      const stringValue = String(val ?? "");
                      const getCellAlign = () => {

                        if (col.align) return col.align;

                        
                        const tipo = col.tipo;
                        const format = col.format;
                        if (format === 'money') return tabla_ui?.columnAlignment?.money || 'right';
                        if (format === 'percentage') return tabla_ui?.columnAlignment?.percentage || 'right';
                        return tabla_ui?.columnAlignment?.[tipo] || columnsConfig.align || 'left';
                      };
                      return (
                        <td
                          key={cIdx}
                          className={columnsConfig.truncateText ? "whitespace-nowrap overflow-hidden text-ellipsis" : ""}
                          style={{
                            width: col.width || 'auto',
                            paddingLeft: '0.5rem',
                            paddingRight: '0.5rem',
                            paddingTop: '0.125rem',
                            paddingBottom: '0.125rem',
                            textAlign: getCellAlign(),
                            fontSize: rowsConfig.fontSize || '0.75rem',
                            fontFamily: rowsConfig.fontFamily || 'inherit',
                            color: rowsConfig.textColor || '#374151',
                            maxWidth: columnsConfig.maxWidth || '180px',
                            borderRight: rowsConfig.borderRight || '1px solid #e5e7eb'
                          }}
                          //title={col.tooltip_field && row[col.tooltip_field] ? row[col.tooltip_field] : stringValue}
                          onMouseEnter={(e) => {
                            // Sin tooltips nuevos mientras el ContextMenu este abierto.
                            if (showFiltros || contextMenuAbierto) return;
                            if (col.tooltip_fields?.length > 0 && evaluarCondicion(col.tooltip_when, row)) {
                              // tooltip_only_if_truncated: solo mostrar si la celda esta
                              // REALMENTE cortada por CSS ellipsis (scrollWidth > clientWidth
                              // del <td> real) -- evita el tooltip redundante cuando el texto
                              // completo ya cabe visible. Sin el flag, comportamiento identico
                              // a hoy (motivo_cancelacion/nota_reserva/nota_estado siguen
                              // mostrandose siempre que tooltip_when se cumpla, sin importar
                              // si SU celda esta truncada -- son diagnostico, no una expansion
                              // del mismo texto).
                              if (col.tooltip_only_if_truncated) {
                                const el = e.currentTarget;
                                if (el.scrollWidth <= el.clientWidth) return;
                              }
                              setActiveTooltip({ col, row, x: e.clientX, y: e.clientY });
                            }
                          }}
                          onMouseLeave={() => setActiveTooltip(null)}
                        >
                          {displayValue}
                        </td>
                      );
                    })}
                    {(!selectionMode && tabla_ui?.actionsColumn?.show !== false && hayAccionesVisibles) && (
                      <td
                        className="whitespace-nowrap"
                        onContextMenu={(e) => e.stopPropagation()}
                        style={{
                          paddingLeft: tabla_ui?.actionsColumn?.cell?.padding?.x || '0.75rem',
                          paddingRight: tabla_ui?.actionsColumn?.cell?.padding?.x || '0.75rem',
                          paddingTop: tabla_ui?.actionsColumn?.cell?.padding?.y || '0.25rem',
                          paddingBottom: tabla_ui?.actionsColumn?.cell?.padding?.y || '0.25rem'
                        }}
                      >
                        <div
                          className="flex items-center"
                          style={{
                            justifyContent: tabla_ui?.actionsColumn?.cell?.align === 'left'
                              ? 'flex-start'
                              : tabla_ui?.actionsColumn?.cell?.align === 'center'
                              ? 'center'
                              : 'flex-end'
                          }}
                        >
                          {(() => {
                            if (filaEsContinuacionDeGrupo) return null;
                            const accionesRow = accionesMenu.filter(a => evaluarCondicion(a.visible_when, row));
                            return accionesRow.length > 0 && (
                              <DropdownMenu
                                item={row}
                                accionesDisponibles={accionesRow}
                                onAccionClick={handleAccionClick}
                                onOpen={() => { setActiveTooltip(null); setContextMenu(null); }}



                                // se agrego al hacer click en la columna del menu acciones debe de colorear la linea.
                                onRowClick={() => handleRowClick(row)}


                              />
                            );
                          })()}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

  const handleRowClick = useCallback((row) => {
    if (!row) return;
    const rowId = row[idField];
    if (String(selectedRowId) === String(rowId)) return;
    setSelectedRowId(rowId);
    if (onRowSelect) {
      onRowSelect(row);
    }
  }, [idField, onRowSelect, selectedRowId]);

  // ==========================================
  // BLOQUEO — sin configuración de invepara
  // No renderiza Toolbar ni grid, solo el modal (y un placeholder detrás
  // si el usuario lo cierra) -- mismo criterio que los errores de schema
  // en SqlexecBuilder (bloqueo estructural, no recuperable sin admin).
  // ==========================================
  if (inveparaBlocked) {
    return (
      <>
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-xs">
          <Database size={18} className="text-gray-400" />
          <span className="text-gray-500">
            Acceso bloqueado — falta configuración de inventario para esta sucursal
          </span>
        </div>
        <SqlexecMensaje
          show={showMensaje}
          type={mensajeData.type}
          title={mensajeData.title}
          message={mensajeData.message}
          steps={mensajeData.steps}
          technicalInfo={mensajeData.technicalInfo}
          actions={[
            {
              label: 'Entendido',
              variant: 'primary',
              onClick: () => setShowMensaje(false)
            }
          ]}
        />
      </>
    );
  }

  // ==========================================
  // RENDER PRINCIPAL
  // ==========================================

  return (
    <div 
      className="flex-1 flex min-h-0"
      style={{
        flexDirection: tabla_ui?.container?.layout === 'flex-column' ? 'column' : 'row'
      }}
    >
      <Toolbar />
      
      <div 
        className="flex"
        style={{
          flex: 1,
          flexDirection: 'column',
          paddingBottom: tabla_ui?.footer?.layout?.strategy === 'flex-auto' ? 0 : '3rem',
          overflow: tabla_ui?.footer?.layout?.strategy === 'flex-auto' ? 'hidden' : 'auto'
        }}
      >
        <div 
          ref={tableScrollRef}
          onScroll={() => setActiveTooltip(null)}
          style={{
            flex: tabla_ui?.footer?.layout?.strategy === 'flex-auto' ? 1 : 'none',
            overflowY: tabla_ui?.footer?.layout?.strategy === 'flex-auto' 
              ? (tabla_ui?.container?.overflowY || 'auto') 
              : 'visible',
            overflowX: tabla_ui?.container?.overflowX || 'auto',
            scrollBehavior: tabla_ui?.container?.scrollBehavior || 'auto',
            position: 'relative'
          }}
        >
          {loading && (
            <div 
              className="absolute inset-0 flex justify-center items-center"
              style={{ 
                backgroundColor: tabla_ui?.loadingOverlay?.backgroundColor || 'rgba(255,255,255,0.9)',
                zIndex: tabla_ui?.loadingOverlay?.zIndex || 10
              }}>
              <div 
                className="animate-spin rounded-full"
                style={{
                  width:       tabla_ui?.loadingOverlay?.spinner?.size        || '2rem',
                  height:      tabla_ui?.loadingOverlay?.spinner?.size        || '2rem',
                  borderWidth: tabla_ui?.loadingOverlay?.spinner?.borderWidth || '2px',
                  borderStyle: 'solid',
                  borderColor: 'transparent',
                  borderTopColor: tabla_ui?.loadingOverlay?.spinner?.borderColor || '#2563eb'
                }}
              />
            </div>
          )}
          {renderDataTable()}
        </div>

        {/* Identificador SQLEXEC cuando el pie esta oculto (ej. detalle del split-panel) */}
        {!loading && showFooterProp === false && panel_ui?.identificador?.visible === true && (
          <div style={{ textAlign: 'right', lineHeight: 1 }}>
            <SqlexecIdTag idsqlexec={idsqlexec} componentForm={componentForm} config={panel_ui?.identificador} />
          </div>
        )}

        {/* Footer - Solo botón "Cargar más" y copyright */}
        {!loading && showFooterProp !== false && (
          <div 
            style={{
              position: tabla_ui?.footer?.layout?.strategy === 'sticky' 
                ? 'sticky' 
                : tabla_ui?.footer?.layout?.strategy === 'fixed' 
                  ? 'fixed' 
                  : 'relative',
              
              bottom: (tabla_ui?.footer?.layout?.strategy === 'sticky' || tabla_ui?.footer?.layout?.strategy === 'fixed') 
                ? 0 
                : 'auto',
              
              left: tabla_ui?.footer?.layout?.strategy === 'fixed' ? 0 : 'auto',
              right: tabla_ui?.footer?.layout?.strategy === 'fixed' ? 0 : 'auto',
              
              zIndex: (tabla_ui?.footer?.layout?.strategy === 'sticky' || tabla_ui?.footer?.layout?.strategy === 'fixed') 
                ? 10 
                : 'auto',
              
              paddingLeft: tabla_ui?.footer?.padding?.x || '2rem',
              paddingRight: tabla_ui?.footer?.padding?.x || '2rem',
              paddingTop: tabla_ui?.footer?.padding?.y || '0.5rem',
              paddingBottom: tabla_ui?.footer?.padding?.y || '0.5rem',
              backgroundColor: tabla_ui?.footer?.backgroundColor || '#f3f4f6',
              backdropFilter: `blur(${tabla_ui?.footer?.backdropBlur || '0px'})`,
              borderTop: tabla_ui?.footer?.borderTop || '1px solid #e5e7eb'
            }}
          >
            <div 
              style={{
                display: tabla_ui?.footer?.layout?.display || 'grid',
                gridTemplateColumns: `repeat(${tabla_ui?.footer?.layout?.columns || 3}, minmax(0, 1fr))`,
                gap: tabla_ui?.footer?.layout?.gap || '1rem',
                alignItems: tabla_ui?.footer?.layout?.alignItems || 'center',
                fontSize: tabla_ui?.footer?.fontSize || '0.75rem',
                fontFamily: tabla_ui?.footer?.fontFamily || 'inherit',
                color: tabla_ui?.footer?.textColor || '#374151'
              }}
            >
              {/* Columna 1: Registros cargados */}
              <div
                style={{
                  textAlign: 'left',
                  color:
                    tabla_ui?.footer?.loadMoreColor || '#10b981',

                  fontWeight:
                    tabla_ui?.footer?.paginationButtons?.fontWeight || '500'
                }}
              >
                <span>
                  {(tabla_ui?.labels?.loadedRecords || '{count} registros cargados')
                    .replace('{count}', data.length)}
                </span>
                <span style={{ marginLeft: panel_ui?.identificador?.gap || '0.5rem' }}>
                  <SqlexecIdTag idsqlexec={idsqlexec} componentForm={componentForm} config={panel_ui?.identificador} />
                </span>
              </div>              

              {/* Columna 2: Copyright */}
              <div style={{ flex: 1, textAlign: 'center' }}>
                <span>
                  {(tabla_ui?.labels?.copyright?.replace('{year}', new Date().getFullYear()) || `💪 Powered By Mapasac © ${new Date().getFullYear()}`)
                    .replace('{version}', version ? `Ver. ${version}` : '')}
                </span>
              </div>

              <button
                onClick={handleLoadMore}
                disabled={!hasMore || loading}
                style={{
                  paddingLeft: tabla_ui?.footer?.paginationButtons?.padding?.x || '0.75rem',
                  paddingRight: tabla_ui?.footer?.paginationButtons?.padding?.x || '0.75rem',
                  paddingTop: tabla_ui?.footer?.paginationButtons?.padding?.y || '0.5rem',
                  paddingBottom: tabla_ui?.footer?.paginationButtons?.padding?.y || '0.5rem',
                  borderWidth: '0px',
                  borderStyle: 'none',
                  backgroundColor: 'transparent',
                  color: tabla_ui?.footer?.loadMoreColor || '#10b981',
                  fontSize: tabla_ui?.footer?.fontSize || '0.75rem',
                  fontFamily: tabla_ui?.footer?.fontFamily || 'inherit',
                  fontWeight: tabla_ui?.footer?.paginationButtons?.fontWeight || '500',
                  borderRadius: tabla_ui?.footer?.paginationButtons?.borderRadius || '6px',
                  transitionDuration: '150ms',
                  cursor: (!hasMore || loading) ? 'not-allowed' : 'pointer',
                  opacity: (!hasMore || loading) ? (tabla_ui?.footer?.paginationButtons?.disabledOpacity || '0.5') : '1',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => {
                  if (hasMore && !loading) {
                    e.target.style.color = tabla_ui?.footer?.loadMoreHoverColor || '#059669';
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasMore && !loading) {
                    e.target.style.color = tabla_ui?.footer?.loadMoreColor || '#10b981';
                  }
                }}
                title={tabla_ui?.labels?.loadMoreTitle || (hasMore ? 'Cargar más registros' : 'No hay más registros')}
              >
                <span>
                  {loading 
                    ? (tabla_ui?.labels?.loading || 'Cargando...') 
                    : (tabla_ui?.labels?.loadMore || 'Cargar más')}
                </span>
                <ChevronRight size={16} />
              </button>

            </div>
          </div>
        )}
      </div>

      {/* Modal: Crear */}
      {showFormModal && (
        <>
          {getEsCatalogo(componentForm) === 1 ? (
            <SqlexecBuilder
              key="create-modal"
              componentForm={componentForm}
              mode="create"
              parentRow={parentRow}
              onClose={handleCloseModal}
              onSuccess={handleSuccessModal}
            />
          ) : (
            <SqlexecMasterDetail
              key="masterdetail-modal"
              componentForm={componentForm}
              mode="create"
              parentRow={parentRow}
              userPermissionLevel={userPermissionLevel}
              onClose={handleCloseModal}
              onSuccess={handleSuccessModal}
              useGenerico={dynamicModalConfig?.use_generico || false}
              idreporte={dynamicModalConfig?.idreporte || null}
            />
          )}
        </>
      )}
      
      {/* Modal: Ver detalles */}
      {showViewModal && selectedItem && (
        getEsCatalogo(componentForm) === 1
          ? <SqlexecBuilder componentForm={componentForm} mode="view" initialData={selectedItem} parentRow={parentRow} onClose={() => { if (tableContainerRef.current && savedVerticalScroll.current > 0) { setTimeout(() => { if (tableContainerRef.current) { tableContainerRef.current.scrollTop = savedVerticalScroll.current; } }, 50); } setShowViewModal(false); setSelectedItem(null); }} />
          : <SqlexecMasterDetail key="view-masterdetail-modal" componentForm={componentForm} mode="view" initialData={selectedItem} userPermissionLevel={userPermissionLevel} onClose={() => { if (tableContainerRef.current && savedVerticalScroll.current > 0) { setTimeout(() => { if (tableContainerRef.current) { tableContainerRef.current.scrollTop = savedVerticalScroll.current; } }, 50); } setShowViewModal(false); setSelectedItem(null); }} />
      )}

      {/* Modal: Editar */}
      {showEditModal && selectedItem && (
        getEsCatalogo(selectedItem._component_form_override || componentForm) === 1
          ? <SqlexecBuilder
            componentForm={selectedItem._component_form_override || componentForm}
            mode="update"
            initialData={selectedItem} 
            parentRow={parentRow} 
            onClose={() => { 
              if (tableContainerRef.current && savedVerticalScroll.current > 0) { 
                setTimeout(() => { 
                  if (tableContainerRef.current) { 
                    tableContainerRef.current.scrollTop = savedVerticalScroll.current; 
                  } 
                }, 50); 
              } 
              setShowEditModal(false); 
              setSelectedItem(null); 
            }} 
            onSuccess={async () => {
              // Con override (ej. Editar Producto/Unidad desde un split panel),
              // selectedItem es el registro del catalogo relacionado -- refreshRecord
              // no aplica (ni el id ni, para queries con parentKey propio como
              // Cxc_Ventas_RemisionPartidas, el endpoint que usa son compatibles).
              // refresh() recarga esta tabla completa por su propio parentKey/filtro,
              // que ya sabe traer los datos correctos -- la fila se identifica por su
              // idField real (ej. idclierede), asi que no se pierde "posicion" alguna.
              if (selectedItem._component_form_override) {
                refresh();
              } else {
                await refreshRecord(componentForm, selectedItem[idField], idField);
              }
              requestAnimationFrame(() => {
                if (tableContainerRef.current && savedVerticalScroll.current > 0) {
                  tableContainerRef.current.scrollTop = savedVerticalScroll.current;
                  savedVerticalScroll.current = 0;
                }
              });
              setShowEditModal(false);
              setSelectedItem(null);
            }}
            />

          : <SqlexecMasterDetail
            key="update-masterdetail-modal"
            componentForm={componentForm}
            mode="update"
            initialData={selectedItem}
            userPermissionLevel={userPermissionLevel}
            surtirMode={selectedItem?._surtir_mode}
            onClose={() => {
              if (tableContainerRef.current && savedVerticalScroll.current > 0) {
                setTimeout(() => {
                  if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = savedVerticalScroll.current;
                  }
                }, 50);
              }
              setShowEditModal(false);
              setSelectedItem(null);
            }}
            onSuccess={async () => { 
              await refreshRecord(componentForm, selectedItem[idField], idField);
              requestAnimationFrame(() => { 
                if (tableContainerRef.current && savedVerticalScroll.current > 0) { 
                  tableContainerRef.current.scrollTop = savedVerticalScroll.current; 
                  savedVerticalScroll.current = 0; 
                } 
              });
              // 🔑 refresh_tabs — avisar tabs huérfanos
              if (selectedItem?._refresh_tabs) {
                const tabs = Array.isArray(selectedItem._refresh_tabs)
                  ? selectedItem._refresh_tabs
                  : [selectedItem._refresh_tabs];
                tabs.forEach(tab => {
                  window.dispatchEvent(new CustomEvent('sqlexec:refresh', { detail: tab }));
                });
              }
              setShowEditModal(false); 
              setSelectedItem(null); 
            }}
          />
      )}

      {/* Modal: Eliminar */}
      {showDeleteModal && selectedItem && (
        <SqlexecBuilder
          componentForm={componentForm}
          mode="delete"
          initialData={selectedItem}
          schemaTitle={schema?.title || componentForm}
          confirmSimple={schema?.confirm_simple === true}
          autorizado={accionAutorizada}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedItem(null);
            setAccionAutorizada(false);
          }}
          onSuccess={() => {
            refresh();
            setShowDeleteModal(false);
            setSelectedItem(null);
            setAccionAutorizada(false);
          }}
        />
      )}

      {/* Modal: Autorizacion por contrasena -- acciones sensibles */}
      {showPasswordAuth && pendingAuthAccion && (
        <SqlexecPasswordAuthDialog
          isOpen={true}
          componentForm={componentForm}
          accionLabel={pendingAuthAccion.accion?.label || 'esta accion'}
          mensajeConfirmacion={
            pendingAuthAccion.accion?.confirm?.pregunta
            || (pendingAuthAccion.accion?.modal_mode === 'delete'
                ? `¿Deseas eliminar ${schema?.title || componentForm}?`
                : `¿Deseas ${(pendingAuthAccion.accion?.label || 'continuar').toLowerCase()} este registro?`)
          }
          onClose={() => { setShowPasswordAuth(false); setPendingAuthAccion(null); }}
          onAuthorized={() => {
            const { accion, item } = pendingAuthAccion;
            setShowPasswordAuth(false);
            setPendingAuthAccion(null);
            handleAccionClick({ ...accion, _autorizado: true }, item);
          }}
        />
      )}

      {/* Modal: Confirmar Exportación */}
      {showConfirmaExport && (
        <SqlexecConfirmDialog
          isOpen={true}
          tipo="warning"
          pregunta={`¿Deseas exportar la información a ${pendingExport?.titulo || pendingExport?.label || 'este formato'}?`}
          onClose={() => {
            setShowConfirmaExport(false);
            setPendingExport(null);
          }}
          onConfirm={() => {
            const accion = { ...pendingExport, confirma: false };
            setShowConfirmaExport(false);
            setPendingExport(null);
            handleAccionClick(accion, selectedItem);
          }}
        />
      )}

      {showConfirmaAccion && pendingAccion?.confirm?.tipo && pendingAccion?.confirm?.pregunta && (
        <SqlexecConfirmDialog
          isOpen={true}
          tipo={pendingAccion.confirm.tipo}
          pregunta={pendingAccion.confirm.pregunta}
          onClose={() => { setShowConfirmaAccion(false); setPendingAccion(null); }}
          onConfirm={async () => {
            const accion = pendingAccion;
            const item = selectedItem;
            setShowConfirmaAccion(false);
            setPendingAccion(null);
            await ejecutarAccion(accion, item);
            setSelectedRowId(null);
            setSelectedItem(null);
          }}
        />
      )}

      {showConfirmaAccion && !(pendingAccion?.confirm?.tipo && pendingAccion?.confirm?.pregunta) && (
        <SqlexecMasterDetail
          key="confirm-accion"
          componentForm="Sys_Confirma_Mensaje"
          mode="create"
          useGenerico={true}
          initialData={{
            idconfirma: 1,
            mode:   pendingAccion?.confirma_mode || 'create_catalogo',
            titulo: pendingAccion?.titulo || '',
          }}
          onClose={() => {
            setShowConfirmaAccion(false);
            setPendingAccion(null);
          }}
          onSuccess={async () => {
            const accion = pendingAccion;
            const item = selectedItem;
            setShowConfirmaAccion(false);
            setPendingAccion(null);
            await ejecutarAccion(accion, item);
            //limpiar
            setSelectedRowId(null);
            setSelectedItem(null);
          }}
        />
      )}

      {showMotivo && pendingAccion && (
        <SqlexecMasterDetail
          key="sys-motivo"
          componentForm={pendingAccion?.motivo_component_form || 'Sys_Motivo'}
          mode="create"
          useGenerico={true}
          initialData={{
            idmotivo: null,
            _title_override: pendingAccion?.titulo || 'Cancelación',
            motivo: '',
            ...(pendingAccion?.motivo_context_fields || []).reduce(
              (acc, f) => ({ ...acc, [f]: selectedItem?.[f] }), {}
            )
          }}
          onClose={() => { setShowMotivo(false); setPendingAccion(null); }}
          onSuccess={async (motivo) => {
            const accion = pendingAccion;
            const item   = selectedItem;
            setShowMotivo(false);
            setPendingAccion(null);
            await ejecutarCancelacion(accion, item, motivo);
            setSelectedRowId(null);
            setSelectedItem(null);
          }}
        />
      )}

      {/* CONTEXT MENU — RIGHT CLICK */}
      {contextMenuAbierto && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          row={contextMenu.row}
          acciones={accionesContextMenu}
          onAccionClick={handleAccionClick}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* UNIVERSAL TOOLTIP */}
      {activeTooltip && (
        <UniversalTooltip
          col={activeTooltip.col}
          row={activeTooltip.row}
          x={activeTooltip.x}
          y={activeTooltip.y}
          tabla_ui={tabla_ui}
        />
      )}

      {/* PANEL FILTROS */}
      {showFiltros && toolbarButtons.find(b => b.type === 'filter') && (() => {
        const filtrarConfig = toolbarButtons.find(b => b.type === 'filter');
        const campos = filtrarConfig?.campos || [];
        const fp = panel_ui?.filterPanel || {};
        const fpHeader = fp.header || {};
        const fpCampos = fp.campos || {};
        const fpInput = fpCampos.input || {};
        const fpLabel = fpCampos.label || {};
        const fpFooter = fp.footer || {};
        const fpLimpiar = fpFooter.limpiar || {};
        const campoActivoStyle = fp?.campo_activo || {};
        const esCampoActivo = (campo, tipo) => {
          if (tipo === 'text')      return !!filtrosValores[campo]?.trim();
          if (tipo === 'select')    return !!filtrosValores[campo];
          if (tipo === 'daterange') return !!(filtrosValores[`${campo}_desde`] || filtrosValores[`${campo}_hasta`]);
          if (tipo === 'numrange')  return !!(filtrosValores[`${campo}_desde`] || filtrosValores[`${campo}_hasta`]);
          return false;
        };

        const filtrosPreviewCount = (() => {
          let total = 0;
          campos.forEach(c => {
            if (c.tipo === 'text' && filtrosValores[c.campo]?.trim()) total++;
            if (c.tipo === 'select' && filtrosValores[c.campo]) total++;
            if (c.tipo === 'daterange') {
              if (filtrosValores[`${c.campo}_desde`] || filtrosValores[`${c.campo}_hasta`]) total++;
            }
            if (c.tipo === 'numrange') {
              const d = filtrosValores[`${c.campo}_desde`];
              const h = filtrosValores[`${c.campo}_hasta`];
              if ((d !== undefined && d !== '') || (h !== undefined && h !== '')) total++;
            }
          });
          return total;
        })();

        // daterange con "requerido": true -- Aplicar queda deshabilitado mientras
        // falte la fecha desde o la fecha hasta (solo en el panel, sin backend).
        const faltanFechasRequeridas = campos.some(c =>
          c.tipo === 'daterange' && c.requerido &&
          (!filtrosValores[`${c.campo}_desde`] || !filtrosValores[`${c.campo}_hasta`]));

        // En esos mismos paneles, Limpiar solo con algo capturado en el panel o
        // con un filtro aplicado en la grilla; los demas paneles, como siempre.
        const limpiarDeshabilitado = campos.some(c => c.tipo === 'daterange' && c.requerido)
          && filtrosPreviewCount === 0 && filtrosActivos === 0;

        const inputStyle = {
          height:       fpInput.height       || '32px',
          fontSize:     fpInput.fontSize     || '0.75rem',
          border:       `1px solid ${fpInput.borderColor || '#d1d5db'}`,
          borderRadius: fpInput.borderRadius || '6px',
          padding:      '0 0.5rem'
        };

        return (
          <div style={{
            position:        'fixed',
            top:             0,
            right:           0,
            width:           fp.width           || '320px',
            height:          'calc(100vh / var(--ui-escala, 1))',
            backgroundColor: fp.backgroundColor || '#ffffff',
            borderLeft:      fp.borderLeft      || '1px solid #e5e7eb',
            boxShadow:       '-4px 0 12px rgba(0,0,0,0.08)',
            zIndex:          fp.zIndex          || 50,
            display:         'flex',
            flexDirection:   'column'
          }}>

            {/* HEADER */}
            <div style={{
              display:         'flex',
              alignItems:      'center',
              justifyContent:  'space-between',
              padding:         fpHeader.padding         || '0.75rem 1rem',
              borderBottom:    fpHeader.borderBottom    || '1px solid #e5e7eb',
              backgroundColor: fpHeader.backgroundColor || '#f3f4f6'
            }}>
              <span style={{
                fontWeight: fpHeader.fontWeight || '600',
                fontSize:   fpHeader.fontSize   || '0.875rem',
                color:      fpHeader.color      || '#111827'
              }}>
                Filtros
              </span>
              <button
                onClick={() => setShowFiltros(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: fpHeader.iconColor || '#6b7280' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* CAMPOS */}
            <div style={{ flex: 1, overflowY: 'auto', padding: fpCampos.padding || '1rem' }}>
              {campos.map((campo, idx) => (
                <div key={idx} style={{ marginBottom: fpCampos.gap || '1rem' }}>
                  <label style={{
                    fontSize:    fpLabel.fontSize    || '0.75rem',
                    fontWeight:   esCampoActivo(campo.campo, campo.tipo) ? campoActivoStyle.fontWeight : fpLabel.fontWeight  || '500',
                    color:        esCampoActivo(campo.campo, campo.tipo) ? campoActivoStyle.labelColor  : fpLabel.color      || '#374151',
                    display:     'block',
                    marginBottom: '0.25rem'
                  }}>
                    {campo.label}
                  </label>

                  {/* TEXT */}
                  {campo.tipo === 'text' && (
                    <input
                      type="text"
                      value={filtrosValores[campo.campo] || ''}
                      onChange={e => setFiltrosValores(prev => ({ ...prev, [campo.campo]: e.target.value }))}
                      style={{
                        width: '100%', 
                        ...inputStyle,
                        border: esCampoActivo(campo.campo, campo.tipo) 
                          ? `2px solid ${campoActivoStyle.borderColor}` 
                          : inputStyle.border
                       }}
                    />
                  )}

                  {/* SELECT */}
                  {campo.tipo === 'select' && (
                    <select
                      value={filtrosValores[campo.campo] || ''}
                      onChange={e => setFiltrosValores(prev => ({ ...prev, [campo.campo]: e.target.value }))}
                      style={{
                        width: '100%', 
                        ...inputStyle,
                        border: esCampoActivo(campo.campo, campo.tipo) 
                          ? `2px solid ${campoActivoStyle.borderColor}` 
                          : inputStyle.border
                       }}
                    >
                      <option value="">-- Todos --</option>
                      {campo.opciones?.map((op, i) => (
                        <option key={i} value={op.value}>{op.label}</option>
                      ))}
                    </select>
                  )}

                  {/* DATE RANGE */}
                  {campo.tipo === 'daterange' && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input type="date"
                        value={filtrosValores[`${campo.campo}_desde`] || ''}
                        onChange={e => setFiltrosValores(prev => ({ ...prev, [`${campo.campo}_desde`]: e.target.value }))}
                        style={{
                          flex: 1, 
                          ...inputStyle,
                          border: esCampoActivo(campo.campo, campo.tipo) 
                            ? `2px solid ${campoActivoStyle.borderColor}` 
                            : inputStyle.border
                        }}
                      />
                      <input type="date"
                        value={filtrosValores[`${campo.campo}_hasta`] || ''}
                        onChange={e => setFiltrosValores(prev => ({ ...prev, [`${campo.campo}_hasta`]: e.target.value }))}
                        style={{
                          flex: 1, 
                          ...inputStyle,
                          border: esCampoActivo(campo.campo, campo.tipo) 
                            ? `2px solid ${campoActivoStyle.borderColor}` 
                            : inputStyle.border
                        }}
                      />
                    </div>
                  )}

                  {/* NUM RANGE */}
                  {campo.tipo === 'numrange' && (
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <input type="number" placeholder="Desde"
                        value={filtrosValores[`${campo.campo}_desde`] || ''}
                        onChange={e => setFiltrosValores(prev => ({ ...prev, [`${campo.campo}_desde`]: e.target.value }))}
                        style={{
                          width: 'calc(50% - 0.25rem)', 
                          ...inputStyle,
                          border: esCampoActivo(campo.campo, campo.tipo) 
                            ? `2px solid ${campoActivoStyle.borderColor}` 
                            : inputStyle.border
                        }}
                      />
                      <input type="number" placeholder="Hasta"
                        value={filtrosValores[`${campo.campo}_hasta`] || ''}
                        onChange={e => setFiltrosValores(prev => ({ ...prev, [`${campo.campo}_hasta`]: e.target.value }))}
                        style={{
                          width: 'calc(50% - 0.25rem)', 
                          ...inputStyle,
                          border: esCampoActivo(campo.campo, campo.tipo) 
                            ? `2px solid ${campoActivoStyle.borderColor}` 
                            : inputStyle.border
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* FOOTER */}
            <div style={{
              padding:    fpFooter.padding    || '0.75rem 1rem',
              borderTop:  fpFooter.borderTop  || '1px solid #e5e7eb',
              display:    'flex',
              gap:        fpFooter.gap        || '0.5rem'
            }}>
              {/* LIMPIAR */}
              <button
                disabled={limpiarDeshabilitado}
                onClick={() => {
                  setFiltrosValores({});
                  setFiltrosActivos(0);
                  setFilters([]);
                  setShowFiltros(false);
                }}
                style={{
                  flex: 1,
                  height:          fpInput.height          || '32px',
                  fontSize:        fpInput.fontSize         || '0.75rem',
                  fontWeight:      '500',
                  border:          `1px solid ${fpLimpiar.borderColor      || '#d1d5db'}`,
                  borderRadius:    fpInput.borderRadius     || '6px',
                  backgroundColor: limpiarDeshabilitado
                    ? (fpLimpiar.disabledBackgroundColor || botones_ui?.secondary?.disabledBackgroundColor || '#f3f4f6')
                    : (fpLimpiar.backgroundColor || '#ffffff'),
                  color:           limpiarDeshabilitado
                    ? (fpLimpiar.disabledColor || botones_ui?.secondary?.disabledTextColor || '#9ca3af')
                    : (fpLimpiar.color || '#374151'),
                  cursor:          limpiarDeshabilitado ? 'not-allowed' : 'pointer'
                }}
              >
                Limpiar
              </button>

              {/* APLICAR */}
              <button
                disabled={faltanFechasRequeridas}
                onClick={() => {
                  const campos = toolbarButtons.find(b => b.type === 'filter')?.campos || [];
                  const filtrosArray = [];
                  let activos = 0;
                  campos.forEach(c => {
                    if (c.tipo === 'text' && filtrosValores[c.campo]?.trim()) {
                      filtrosArray.push({ campo: c.campo, tipo: 'text', valor: filtrosValores[c.campo].trim() });
                      activos++;
                    }
                    if (c.tipo === 'select' && filtrosValores[c.campo]) {
                      filtrosArray.push({ campo: c.campo, tipo: 'select', valor: filtrosValores[c.campo] });
                      activos++;
                    }
                    if (c.tipo === 'daterange') {
                      const desde = filtrosValores[`${c.campo}_desde`];
                      const hasta = filtrosValores[`${c.campo}_hasta`];
                      if (desde || hasta) { filtrosArray.push({ campo: c.campo, tipo: 'daterange', desde, hasta, formato: c.formato }); activos++; }
                    }
                    if (c.tipo === 'numrange') {
                      const desde = filtrosValores[`${c.campo}_desde`];
                      const hasta = filtrosValores[`${c.campo}_hasta`];
                      if ((desde !== undefined && desde !== '') || (hasta !== undefined && hasta !== '')) {
                        filtrosArray.push({ campo: c.campo, tipo: 'numrange', desde, hasta });
                        activos++;
                      }
                    }
                  });
                  //console.log('🔵 FILTRO APLICADO — componentForm:', componentForm, '| parentKey:', parentKey, '| parentValue:', parentValue);
                  //console.log('🔵 filtrosArray construido:', filtrosArray);
                  setFiltrosActivos(activos);
                  setFilters(filtrosArray);
                  setShowFiltros(false);
                }}
                style={{
                  flex:            1,
                  height:          fpInput.height                          || '32px',
                  fontSize:        fpInput.fontSize                        || '0.75rem',
                  fontWeight:      '500',
                  border:          'none',
                  borderRadius:    fpInput.borderRadius                    || '6px',
                  backgroundColor: faltanFechasRequeridas
                    ? (botones_ui?.primary?.disabledBackgroundColor || botones_ui?.disabledBackgroundColor || '#9ca3af')
                    : (botones_ui?.primary?.backgroundColor || '#3b82f6'),
                  color:           faltanFechasRequeridas
                    ? (botones_ui?.primary?.disabledTextColor || botones_ui?.disabledTextColor || '#ffffff')
                    : (botones_ui?.primary?.textColor || '#ffffff'),
                  cursor:          faltanFechasRequeridas ? 'not-allowed' : 'pointer'
                }}
              >
                Aplicar {filtrosPreviewCount > 0 ? `(${filtrosPreviewCount})` : ''}
              </button>
            </div>

          </div>
        );
      })()}

      {/* Modal de mensajes */}
      <SqlexecMensaje
        show={showMensaje}
        type={mensajeData.type}
        title={mensajeData.title}
        message={mensajeData.message}
        steps={mensajeData.steps}
        technicalInfo={mensajeData.technicalInfo}
        actions={[
          {
            label: 'Entendido',
            variant: 'primary',
            onClick: () => setShowMensaje(false)
          }
        ]}
        onClose={() => setShowMensaje(false)}
      />

      {/* Modal GridMultiSelect */}
      {(() => {
        if (showMultiSelect && schema?.multiselect?.enabled) {
          return (
            <GridMultiSelect
              config={schema.multiselect}
              parentValue={parentValue}
              parentRow={parentRow}
              componentForm={componentForm}
              schema={schema}
              onClose={() => setShowMultiSelect(false)}
              onSuccess={() => {
                setShowMultiSelect(false);
                refresh();
              }}
            />
          );
        }
        return null;
      })()}

      {/* Modal FileGalleryManager */}
      {(() => {
        if (showFileGallery && schema?.fileGallery?.enabled) {
          return (
            <FileGalleryManager
              config={schema.fileGallery}
              parentValue={parentValue}
              parentRow={parentRow}
              componentForm={componentForm}
              schema={schema}
              onClose={() => setShowFileGallery(false)}
              onSuccess={() => {
                setShowFileGallery(false);
                refresh();
              }}
            />
          );
        }
        return null;
      })()}

      {/* Modal Dinámico */}
      {showDynamicModal && dynamicModalConfig && (() => {
        if (dynamicModalConfig.componente === 'light') {
          return (
            <SqlexecMasterDetailLight
              key={`dynamic-modal-light-${dynamicModalConfig.component_form}`}
              componentForm={dynamicModalConfig.component_form}
              mode="create"
              parentRow={data.find(r => r[idField] === selectedRowId) || null}
              useGenerico={dynamicModalConfig.use_generico ?? true}
              idreporte={dynamicModalConfig.idreporte || null}
              origenComponentForm={componentForm}
              origenKey={dynamicModalConfig.key}
              autorizado={dynamicModalConfig._autorizado === true}
              accionTag={dynamicModalConfig.key === 'cancelar_cfdi' ? 'cancelar' : null}
              onClose={() => { setShowDynamicModal(false); setDynamicModalConfig(null); setAccionAutorizada(false); }}
              onSuccess={() => {
                setShowDynamicModal(false);
                setDynamicModalConfig(null);
                setAccionAutorizada(false);
                // ← Cerrar SplitPanel — la factura ya se generó, las partidas mostradas quedarían obsoletas
                if (splitPanelActivo) {
                  setSplitPanelActivo(false);
                  window.dispatchEvent(new CustomEvent('sqlexec:splitpanel:closed', { detail: { componentForm } }));
                  window.dispatchEvent(new CustomEvent('sqlexec:splitpanel', { detail: { componentForm, selectedId: null } }));
                }
                // 🔑 refresh_tabs — avisar tabs huérfanos (ej. Facturas tras Generar Factura)
                if (Array.isArray(dynamicModalConfig.refresh_tabs)) {
                  dynamicModalConfig.refresh_tabs.forEach(tab => {
                    window.dispatchEvent(new CustomEvent('sqlexec:refresh', { detail: tab }));
                  });
                }
                refresh();
              }}
              onRefreshGrid={() => refresh()}
            />
          );
        }

        const escatalogo = dynamicModalConfig.escatalogo ?? getEsCatalogo(dynamicModalConfig.component_form);
        return escatalogo === 1
          ? <SqlexecBuilder
              key={`dynamic-modal-${dynamicModalConfig.component_form}`}
              componentForm={dynamicModalConfig.component_form}
              mode="create"
              parentRow={data.find(r => r[idField] === selectedRowId) || null}
              onClose={() => { setShowDynamicModal(false); setDynamicModalConfig(null); }}
              onSuccess={() => { setShowDynamicModal(false); setDynamicModalConfig(null); }}
            />
          : <SqlexecMasterDetail
              key={`dynamic-modal-${dynamicModalConfig.component_form}`}
              componentForm={dynamicModalConfig.component_form}
              mode="create"
              initialData={emailData || {}}
              parentRow={data.find(r => r[idField] === selectedRowId) || null}
              userPermissionLevel={userPermissionLevel}
              useGenerico={dynamicModalConfig.use_generico || false}
              idreporte={dynamicModalConfig.idreporte || null}
              servicioClave={dynamicModalConfig.servicio_clave || null}
              asyncEmail={dynamicModalConfig.async || false}
              onClose={() => { setShowDynamicModal(false); setDynamicModalConfig(null); setEmailData(null); }}
              onSuccess={() => { setShowDynamicModal(false); setDynamicModalConfig(null); setEmailData(null); }}
            />;
      })()}
      
    </div>
  );
};

export default SqlexecTable;